// ProductUnitWisePage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { compactPageItems } from "../../components/ui/compactPagination";
import api from "../../api";
import {
  viewAuthenticatedFile,
  downloadAuthenticatedFile,
  getFileAccessErrorMessage,
} from "../../utils/fileAccess";
import "./ProductUnitWisePage.css";
import "./ProductUnitWisePage.overrides.css";
import "./TechnicianDesignSystem.css";
import UnitChip from "../../components/common/UnitChip";
import CreatableTextSelect from "../../components/CreatableTextSelect";
import AssetValueSummary from "../../components/shared/AssetValueSummary";
import { BLANK_DISPOSAL, DISPOSAL_METHODS, disposalPayload, validateDisposalForm } from "../../utils/assetDisposal";
import { toDateOnlyString } from "../../utils/dateOnly";
import {
  LIFE_UNITS,
  isValidLifeValue,
  endLifeFormState,
  lifecycleLabel,
  lifecycleTone,
} from "../../utils/assetLifecycle";
import { usePermissions } from "../../context/PermissionContext";
import {
  getMonthKey,
  buildMonthOptions,
  monthKeyLabel,
  monthFileSuffix,
  sanitizeFileNamePart,
  csvQuote,
} from "../../utils/monthFilter";

/* ── blank form ── */
const BLANK = {
  name: "",
  category: "",
  brand: "",
  model_no: "",
  serial_numbers: "",
  processor: "",
  ram_gb: "",
  ssd_gb: "",
  hdd_gb: "",
  price: "",
  purchase_date: "",
  warranty_date: "",
  warranty_expiry_date: "",
  qty: 1,
  allocation_qty: 1,
  status: "ACTIVE",
  has_cpu: false,
  has_monitor: false,
  has_mouse: false,
  has_keyboard: false,
  has_adapter: false,
  notes: "",

  // Asset Life (optional; End Life Date is derived from it)
  useful_life_value: "",
  useful_life_unit: "YEARS",

  // invoice fields
  invoice_number: "",
  vendor_company_name: "",
  vendor_email: "",
  vendor_phone: "",
  purchase_company_id: "",
  purchase_company_name: "",
  invoice_purchase_date: "",
  invoice_amount: "",
  invoice_remarks: "",
  invoice_file: null,
};

/* ── helpers ── */
const MAX_INVOICE_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
// Sentinel selectedUnit value meaning "no unit filter -- show everything
// this user's data_scope allows." Never a real unit id, so it can never
// collide with one.
const ALL_UNITS = "ALL";
const money = (v) => {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? `₹${n.toFixed(2)}` : "-";
};

const fmtDate = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Date-only ('YYYY-MM-DD') display that never shifts the calendar day:
// read as Y/M/D, not through new Date(str) (UTC midnight -> local). A
// serialized timestamp is read as its IST day (utils/dateOnly.js).
const fmtDateOnly = (v) => {
  const s = toDateOnlyString(v);
  if (!s) return fmtDate(v);
  const [y, m, d] = s.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", timeZone: "UTC",
  });
};

// Asset Life / End Life: see utils/assetLifecycle.js. End Life is DERIVED by
// the backend (p.expected_end_date); forms only preview it while typing.
// Group rows: one value only when every item agrees, otherwise "Varies".
const groupLifeField = (group, key) => {
  const values = [...new Set((group.items || [group]).map((i) => i[key] ?? null))];
  return values.length === 1 ? values[0] : "VARIES";
};

const LifecycleBadge = ({ lifecycle }) => (
  <span className={`puws-badge life-${lifecycleTone(lifecycle)}`}>{lifecycleLabel(lifecycle)}</span>
);

// Form value for <input type="date">. Date-only strings are kept as-is and
// timestamps are read as their IST day -- never through the browser's own
// timezone, which moved the purchase date back a day west of UTC.
const inputDate = (v) => toDateOnlyString(v) || "";

const parseSerials = (t) =>
  String(t || "")
    .split(/[\n,]+/)
    .map((v) => v.trim())
    .filter(Boolean);

// Phase 0B: the old getApiBaseUrl()/getFileUrl() module-level helpers built a
// direct, unauthenticated `/uploads/...` URL. Vendor invoice files are now
// fetched as an authenticated Blob (see the usePreviewInvoiceFile effect
// inside the component below) and exposed as a short-lived local object URL
// instead — the real API URL never appears in the DOM.

const isPdfFile = (filePath) => String(filePath || "").toLowerCase().endsWith(".pdf");

const isImageFile = (filePath) => /\.(png|jpg|jpeg|webp|gif)$/i.test(String(filePath || ""));

const getFileName = (filePath) => {
  if (!filePath) return "Invoice File";
  try {
    return decodeURIComponent(String(filePath).split("/").pop() || "Invoice File");
  } catch (_e) {
    return String(filePath).split("/").pop() || "Invoice File";
  }
};

const hasInvoiceInput = (data = {}) =>
  !!(
    data.invoice_number ||
    data.vendor_company_name ||
    data.vendor_email ||
    data.vendor_phone ||
    data.purchase_company_id ||
    data.purchase_company_name ||
    data.invoice_purchase_date ||
    data.invoice_amount ||
    data.invoice_remarks ||
    data.invoice_file
  );

const buildInvoiceFormData = (data = {}, fallback = {}) => {
  const fd = new FormData();

  fd.append("invoice_number", data.invoice_number || "");
  fd.append("vendor_company_name", data.vendor_company_name || "");
  fd.append("vendor_email", data.vendor_email || "");
  fd.append("vendor_phone", data.vendor_phone || "");

  fd.append("purchase_company_id", data.purchase_company_id || "");
  fd.append("purchase_company_name", data.purchase_company_name || "");

  fd.append(
    "purchase_date",
    data.invoice_purchase_date || fallback.purchase_date || ""
  );

  fd.append("amount", data.invoice_amount || fallback.price || 0);
  fd.append("remarks", data.invoice_remarks || "");

  if (data.invoice_file) {
    fd.append("invoice_file", data.invoice_file);
  }

  return fd;
};

const extractCreatedProductIds = (data) => {
  if (Array.isArray(data?.products)) {
    return data.products.map((p) => p.id).filter(Boolean);
  }

  if (data?.id) return [data.id];
  if (data?.data?.id) return [data.data.id];

  return [];
};

const specLine = (p) => {
  if (!p) return "-";
  const r = p.ram_gb ? `${p.ram_gb}GB` : "-";
  const s = p.ssd_gb
    ? `${p.ssd_gb}GB SSD`
    : p.hdd_gb
    ? `${p.hdd_gb}GB HDD`
    : "-";

  return `${p.processor || "-"} / ${r} / ${s}`;
};

const productToForm = (p = {}) => ({
  name: p.name || "",
  category: p.category || "",
  brand: p.brand || "",
  model_no: p.model_no || "",
  serial_numbers: p.serial_no || "",
  processor: p.processor || "",
  ram_gb: p.ram_gb ?? "",
  ssd_gb: p.ssd_gb ?? "",
  hdd_gb: p.hdd_gb ?? "",
  price: p.price ?? "",
  purchase_date: inputDate(p.purchase_date),
  warranty_date: inputDate(p.warranty_date || p.purchase_date),
  warranty_expiry_date: inputDate(p.warranty_expiry_date),
  qty: p.qty || 1,
  allocation_qty: p.qty || 1,
  status: p.status || "ACTIVE",
  has_cpu: !!p.has_cpu,
  has_monitor: !!p.has_monitor,
  has_mouse: !!p.has_mouse,
  has_keyboard: !!p.has_keyboard,
  has_adapter: !!p.has_adapter,
  notes: p.notes || "",
  useful_life_value: p.useful_life_value ?? "",
  useful_life_unit: p.useful_life_unit || "YEARS",
});

// Inventory-integrity rule for editing a bulk/non-serialized product's Total
// Qty: the new value must never drop below however many units of this exact
// row are currently out (an active allocation) -- reducing further would
// make the derived available stock (qty - currently_out, computed wherever
// this page shows "Stock"/"Available") go negative and effectively erase
// units that are still legitimately with a technician. `currentlyOut` is
// read straight from the row's own `currently_out` field (already computed
// server-side from the SAME canonical ALLOCATED_QTY_SUBQUERY every other
// stock number on this page uses -- never re-derived here). Existing
// business rule (unchanged by this fix): a product row's qty must be at
// least 1 -- enforced today via this field's own `min="1"`/backend "qty
// must be greater than 0" check -- so even with currentlyOut = 0 the floor
// stays 1, not 0. This is UX-only: services/productController.js#update
// re-validates the same rule from live data regardless of what this says.
function validateEditQty(rawValue, currentlyOut) {
  const trimmed = String(rawValue ?? "").trim();
  if (trimmed === "") return "Total quantity is required.";

  const n = Number(trimmed);
  if (!Number.isFinite(n) || !Number.isInteger(n)) {
    return "Total quantity must be a whole number.";
  }

  const out = Math.max(0, Number(currentlyOut || 0));
  const minQty = Math.max(1, out);

  if (n < minQty) {
    return out > 0
      ? `Total quantity cannot be less than ${out} because ${out} unit(s) are currently allocated.`
      : `Total quantity must be at least ${minQty}.`;
  }

  return "";
}

const Spin = ({ white, lg }) => (
  <span className={`puws-spin${white ? " white" : ""}${lg ? " lg" : ""}`} />
);

const WarrantyBadge = ({ status }) => {
  const v = String(status || "NO_WARRANTY").toUpperCase();

  if (v === "IN_WARRANTY") {
    return <span className="puws-badge warranty">In Warranty</span>;
  }

  if (v === "EXPIRED") {
    return <span className="puws-badge expired">Expired</span>;
  }

  return <span className="puws-badge nowarranty">No Warranty</span>;
};

// Discarded is a permanent lifecycle retirement, NOT the same as the
// reversible Active/Inactive operational status -- same distinction and
// same reasons list as pages/admins/inventroy/InventoryProductList.js's
// Discard modal (the reason codes must match what the backend validator
// accepts -- see validators/productLifecycleValidator.js).
const StatusBadge = ({ status, isDiscarded }) =>
  isDiscarded ? (
    <span className="puws-badge discarded">Discarded</span>
  ) : status === "INACTIVE" ? (
    <span className="puws-badge inactive">Inactive</span>
  ) : (
    <span className="puws-badge active">Active</span>
  );

const todayInputDate = () => new Date().toISOString().slice(0, 10);

const DISCARD_REASONS = [
  ["END_OF_USEFUL_LIFE", "End of Useful Life"],
  ["DAMAGED", "Damaged"],
  ["BEYOND_REPAIR", "Beyond Repair"],
  ["OBSOLETE", "Obsolete"],
  ["UNSERVICEABLE", "Unserviceable"],
  ["LOST", "Lost"],
  ["OTHER", "Other"],
];

/* ════════════════════════════════════════════════ */
export default function ProductUnitWisePage() {
  const { can } = usePermissions();
  // Manager (/manager/products) and technician (/technician/products)
  // panels: no cross-unit "All Units" tab -- the page opens on, and only
  // offers, the caller's own unit tab(s). Every other mount of this page
  // keeps the All Units tab and its default.
  const hideAllUnits =
    window.location.pathname.startsWith("/manager/") ||
    window.location.pathname.startsWith("/technician/");
  const canCreate = can("products", "create");
  const canEdit = can("products", "edit");
  const canDelete = can("products", "delete");
  // The discard feature/backend already exists (PUT /products/:id/discard,
  // controllers/productController.js#discard) and is fully wired up on
  // pages/admins/inventroy/InventoryProductList.js -- this page (mounted at
  // /technician/products and /manager/products, a separate component from
  // that one) never had the button/modal added at all, which is why it
  // never rendered regardless of what products:discard actually resolved
  // to. Never hardcode a role check here -- same permission-driven pattern
  // as canCreate/canEdit/canDelete above.
  const canDiscard = can("products", "discard");
  // Group Edit (every serial item of one group at once): products:edit, or
  // the narrower products:group_edit (Technician, own unit only -- enforced
  // by PUT /products/group-edit, database/phase-81-technician-group-edit).
  // group_edit alone never unlocks single-item edit.
  const canGroupEdit = canEdit || can("products", "group_edit");
  // Add Asset Item (Serial Items modal): products:create or the narrower
  // products:add_item (Technician, own unit only -- enforced by POST
  // /products/:id/group-items, database/phase-87-technician-add-asset-item).
  const canAddItem = canCreate || can("products", "add_item");
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState("");
  const [products, setProducts] = useState([]);
  const [companies, setCompanies] = useState([]); // Purchase company list
  const [vendorCompanies, setVendorCompanies] = useState([]); // Existing vendor list from product_vendor_invoices
  const [showVendorDrop, setShowVendorDrop] = useState(false);

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ACTIVE");
  const [brandFilter, setBrandFilter] = useState("ALL");
  const [holderFilter, setHolderFilter] = useState("ALL");
  const [monthFilter, setMonthFilter] = useState("ALL");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [loadingUnits, setLoadingUnits] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const [showEdit, setShowEdit] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editMode, setEditMode] = useState("single"); // single | group
  const [editForm, setEditForm] = useState(BLANK);
  // Inline "Total Qty" validation message for the single-mode bulk-row edit
  // (inventory-integrity rule: qty may never drop below the row's own
  // currently_out). Populated by validateEditQty(), read by the field's
  // error text and by handleEditSave (which refuses to call the API at all
  // while this is set) -- purely a UX layer; the backend re-checks the same
  // rule from its own live data regardless of what this says.
  const [editQtyError, setEditQtyError] = useState("");

  // Manual Discard (single-item only -- same PUT /products/:id/discard
  // endpoint and modal fields as InventoryProductList.js's Discard modal).
  const [discardTarget, setDiscardTarget] = useState(null);
  const [discardForm, setDiscardForm] = useState({ discard_date: todayInputDate(), reason: "", remarks: "", ...BLANK_DISPOSAL });
  const [discarding, setDiscarding] = useState(false);

  const [showView, setShowView] = useState(false);
  const [viewGroup, setViewGroup] = useState(null);

  // Add Asset Item -- one new serial item in the open Serial Items group.
  const [addItemSource, setAddItemSource] = useState(null);
  const [addItemForm, setAddItemForm] = useState(null);
  const [addItemError, setAddItemError] = useState("");
  const [addingItem, setAddingItem] = useState(false);

  // "Select Product to Discard" -- shown instead of the direct Discard
  // modal whenever a group represents more than one physical item (discard
  // is always per-individual-product-id; a multi-item group has no single
  // id to discard against). Separate from showView/viewGroup above (that
  // modal is a general browse/edit view, not discard-purpose-built), but
  // sourced from the SAME already-fetched `groupedProducts` item list --
  // no new API call.
  const [showDiscardPicker, setShowDiscardPicker] = useState(false);
  const [discardPickerGroup, setDiscardPickerGroup] = useState(null);

  const [showInvoices, setShowInvoices] = useState(false);
  const [invoiceProduct, setInvoiceProduct] = useState(null);
  const [invoiceRows, setInvoiceRows] = useState([]);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [previewInvoiceObjectUrl, setPreviewInvoiceObjectUrl] = useState(null);
  const [previewInvoiceLoading, setPreviewInvoiceLoading] = useState(false);
  const [previewInvoiceError, setPreviewInvoiceError] = useState("");
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [showInvoiceCreate, setShowInvoiceCreate] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({ ...BLANK });
  const [savingInvoice, setSavingInvoice] = useState(false);

  const [deleting, setDeleting] = useState(false);
  const [showAllocHistory, setShowAllocHistory] = useState(false);
  const [allocHistoryProduct, setAllocHistoryProduct] = useState(null);
  const [allocHistoryRows, setAllocHistoryRows] = useState([]);
  const [allocHistorySummary, setAllocHistorySummary] = useState(null);
  const [loadingAllocHistory, setLoadingAllocHistory] = useState(false);
  const [allocHistoryMode, setAllocHistoryMode] = useState("ALL"); // ALL | RETURNED

  // Phase 0B: fetch the vendor invoice file as an authenticated Blob whenever
  // the selected preview invoice changes, instead of pointing <img>/<iframe>
  // at a direct, unauthenticated `/uploads/...` URL.
  useEffect(() => {
    if (!previewInvoice?.id) {
      setPreviewInvoiceObjectUrl(null);
      setPreviewInvoiceError("");
      return;
    }

    let cancelled = false;
    let currentRevoke = null;
    setPreviewInvoiceLoading(true);
    setPreviewInvoiceError("");

    viewAuthenticatedFile(`/files/product-invoices/${previewInvoice.id}/view`)
      .then(({ objectUrl, revoke }) => {
        if (cancelled) {
          revoke();
          return;
        }
        currentRevoke = revoke;
        setPreviewInvoiceObjectUrl(objectUrl);
      })
      .catch((err) => {
        if (cancelled) return;
        setPreviewInvoiceError(getFileAccessErrorMessage(err, "invoice file"));
        setPreviewInvoiceObjectUrl(null);
      })
      .finally(() => {
        if (!cancelled) setPreviewInvoiceLoading(false);
      });

    return () => {
      cancelled = true;
      if (currentRevoke) currentRevoke();
    };
  }, [previewInvoice?.id]);

  const handleDownloadInvoice = async () => {
    if (!previewInvoice?.id) return;
    try {
      await downloadAuthenticatedFile(
        `/files/product-invoices/${previewInvoice.id}/download`,
        getFileName(previewInvoice.invoice_file)
      );
    } catch (err) {
      alert(getFileAccessErrorMessage(err, "invoice file"));
    }
  };

  const fmtDateTime = (v) => {
  if (!v) return "-";



  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "-";

  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

  const [toast, setToast] = useState({
    show: false,
    msg: "",
    variant: "success",
  });

  const pop = (msg, variant = "success") => {
    setToast({ show: true, msg, variant });
    setTimeout(() => setToast((t) => ({ ...t, show: false })), 3000);
  };

  const fetchCompanies = async () => {
  try {
    const res = await api.get(`/invoice-companies`);
    setCompanies(Array.isArray(res.data) ? res.data : []);
  } catch (e) {
    pop(e?.response?.data?.error || "Failed to fetch companies", "danger");
  }
};

const fetchVendorCompanies = async () => {
  try {
    // Vendor names are saved in product_vendor_invoices.vendor_company_name.
    // This function is intentionally flexible because some APIs return an array,
    // while some return { data: [...] } or { rows: [...] }.
    const res = await api.get(`/product-vendor-invoices`);

    const rows = Array.isArray(res.data)
      ? res.data
      : Array.isArray(res.data?.data)
      ? res.data.data
      : Array.isArray(res.data?.rows)
      ? res.data.rows
      : [];

    const map = new Map();

    rows.forEach((row) => {
      const name = String(
        row.vendor_company_name ||
          row.vendorCompanyName ||
          row.vendor_company ||
          row.vendor ||
          row.company_name ||
          row.name ||
          ""
      ).trim();

      if (!name) return;

      const key = name.toLowerCase();
      const email = row.vendor_email || row.vendorEmail || row.email || "";
      const phone =
        row.vendor_phone ||
        row.vendorPhone ||
        row.phone_number ||
        row.phone ||
        "";

      if (!map.has(key)) {
        map.set(key, {
          id: key,
          name,
          email,
          phone_number: phone,
        });
      } else {
        const existing = map.get(key);

        map.set(key, {
          ...existing,
          email: existing.email || email,
          phone_number: existing.phone_number || phone,
        });
      }
    });

    setVendorCompanies(
      Array.from(map.values()).sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""))
      )
    );
  } catch (e) {
    console.error("fetch vendor companies error:", e);
    setVendorCompanies([]);
  }
};

const fetchAllocationHistoryForItems = async (items = []) => {
  const ids = items.map((item) => item.id).filter(Boolean);

  if (!ids.length) {
    setAllocHistoryRows([]);
    setAllocHistorySummary(null);
    return;
  }

  try {
    setLoadingAllocHistory(true);

    const responses = await Promise.all(
      ids.map((productId) =>
        api
          .get(`/products/${productId}/allocation-history?limit=100`)
          .then((res) => ({
            productId,
            data: res.data,
          }))
          .catch(() => ({
            productId,
            data: { items: [], summary: null },
          }))
      )
    );

    const rows = responses.flatMap((r) =>
      (Array.isArray(r.data?.items) ? r.data.items : []).map((item) => ({
        ...item,
        source_product_id: r.productId,
      }))
    );

    // Canonical numbers come from EACH product's own backend-computed
    // `summary` (services/productController.js's getAllocatioProductnHistory,
    // which derives available_qty from products.qty minus current active
    // allocations -- the same inventory model used everywhere else), summed
    // across however many product ids this popup covers. This intentionally
    // does NOT recompute totals from `rows` -- `rows` is capped at
    // `limit` per product for the card/table display and would silently
    // undercount a prolific item's lifetime totals if used for the summary.
    const summary = responses.reduce(
      (acc, r) => {
        const s = r.data?.summary || {};
        acc.total_allocated += Number(s.total_allocated || 0);
        acc.total_returned += Number(s.total_returned || 0);
        acc.currently_out += Number(s.currently_out || 0);
        acc.available_qty += Number(s.available_qty || 0);
        acc.current_holders.push(...(Array.isArray(s.current_holders) ? s.current_holders : []));
        return acc;
      },
      { total_allocated: 0, total_returned: 0, currently_out: 0, available_qty: 0, current_holders: [] }
    );

    summary.current_holders = [...new Set(summary.current_holders)];

    setAllocHistoryRows(rows);
    setAllocHistorySummary(summary);
  } catch (e) {
    pop(e?.response?.data?.error || "Failed to fetch allocation history", "danger");
  } finally {
    setLoadingAllocHistory(false);
  }
};

const openAllocationHistory = async (groupOrItem, mode = "ALL") => {
  const items = Array.isArray(groupOrItem?.items)
    ? groupOrItem.items
    : [groupOrItem];

  setAllocHistoryMode(mode);
  setAllocHistoryProduct({ ...groupOrItem, items });
  setAllocHistoryRows([]);
  setAllocHistorySummary(null);
  setShowAllocHistory(true);

  await fetchAllocationHistoryForItems(items);
};

  /* ── fetch units ── */
  const fetchUnits = async (autoSelect = true) => {
    try {
      setLoadingUnits(true);

      const res = await api.get(
        `/products/unit-wise/summary`
      );

      const data = Array.isArray(res.data) ? res.data : [];
      setUnits(data);

      // No "All Units" tab for technician/manager -- default to the first
      // accessible unit (keep the current selection if it still exists).
      if (autoSelect) {
        setSelectedUnit((prev) =>
          prev && data.some((u) => String(u.unit_id) === String(prev))
            ? prev
            : data[0]
            ? String(data[0].unit_id)
            : ""
        );
      }
    } catch (e) {
      pop(e?.response?.data?.error || "Failed to fetch units", "danger");
    } finally {
      setLoadingUnits(false);
    }
  };

  /* ── fetch products for one specific unit (unchanged) ── */
  const fetchUnitProducts = async (unitId) => {
    try {
      setLoadingProducts(true);

      const res = await api.get(
        `/products/unit-wise/${unitId}`
      );

      setProducts(Array.isArray(res.data) ? res.data : []);
      setPage(1);
    } catch (e) {
      pop(e?.response?.data?.error || "Failed to fetch products", "danger");
    } finally {
      setLoadingProducts(false);
    }
  };

  // All Units: GET /products (productController.getAll) -- the SAME
  // endpoint already used by the Admin/shared Product List, already
  // enforces the caller's own resolved products:view data_scope
  // (ALL/UNIT/SELF/ASSIGNED) server-side, and now returns the same
  // total_allocated/total_returned/currently_out/current_value/unit_name
  // fields the single-unit endpoint does -- no separate "all units" backend
  // concept, no duplicated fetch logic beyond this one function, and no
  // authorization decision made here on the frontend: whatever the backend
  // scope allows is exactly what comes back.
  const fetchAllProducts = async () => {
    try {
      setLoadingProducts(true);

      const res = await api.get(`/products`);

      setProducts(Array.isArray(res.data) ? res.data : []);
      setPage(1);
    } catch (e) {
      pop(e?.response?.data?.error || "Failed to fetch products", "danger");
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
  fetchUnits(true);
  fetchCompanies();
  fetchVendorCompanies();
}, []);;

  useEffect(() => {
    if (selectedUnit === ALL_UNITS) {
      fetchAllProducts();
    } else if (selectedUnit) {
      fetchUnitProducts(selectedUnit);
    } else {
      setProducts([]);
    }
  }, [selectedUnit]);

  useEffect(() => {
    setPage(1);
  }, [query, statusFilter, brandFilter, holderFilter, monthFilter, perPage, selectedUnit]);

  const handleRefresh = async () => {
    await fetchUnits(false);
    if (selectedUnit === ALL_UNITS) await fetchAllProducts();
    else if (selectedUnit) await fetchUnitProducts(selectedUnit);
  };

  /* ── selected unit info ── */
  const selectedUnitInfo = useMemo(
    () => units.find((u) => String(u.unit_id) === String(selectedUnit)) || null,
    [units, selectedUnit]
  );
  // Adding a product requires ONE concrete unit to attach it to -- "All
  // Units" is a view-only filter, never a valid destination for a new
  // product. This is the ONLY thing All Units mode restricts; everything
  // else (viewing, filtering, exporting) works identically to a real unit.
  const hasConcreteUnitSelected = !!selectedUnit && selectedUnit !== ALL_UNITS;

  const getHolderNamesFromRow = (row = {}) => {
    const raw =
      row.current_holder_names ||
      row.current_holders ||
      row.allotted_user_names ||
      row.assigned_user_names ||
      row.holder_names ||
      row.user_name ||
      "";

    if (Array.isArray(raw)) {
      return raw
        .map((value) => String(value || "").trim())
        .filter(Boolean);
    }

    return String(raw || "")
      .split(/[,|\n]+/)
      .map((value) => value.trim())
      .filter(Boolean);
  };

  /* ── grouped products ── */
  const groupedProducts = useMemo(() => {
    const map = new Map();

    products.forEach((p) => {
      const key = [
        // unit_id FIRST: in All Units mode, two otherwise-identical products
        // (same name/spec/price) that physically live in DIFFERENT units are
        // separate stock and must render as separate group rows -- merging
        // them would hide one unit's inventory inside another's count. A
        // no-op for the single-unit views (unit_id is constant there).
        p.unit_id,
        p.name,
        p.category,
        p.brand,
        p.model_no,
        p.processor,
        p.ram_gb,
        p.ssd_gb,
        p.hdd_gb,
        p.price,
        p.purchase_date,
        p.warranty_expiry_date,
        p.warranty_status,
        p.status,
      ]
        .map((v) => String(v ?? "").trim().toLowerCase())
        .join("|");

      const serial = p.serial_no || p.serial_number || "";
      const sku = p.sku || "";

      if (!map.has(key)) {
        map.set(key, {
          ...p,
          group_key: key,
          // A single non-serialized row can itself carry qty > 1 (bulk stock
          // entered as one row, no serial numbers) -- this must be the row's
          // OWN qty, never a hardcoded 1, or a bulk row's real quantity
          // silently disappears from the "Qty: N" pill/Edit-vs-Edit-group
          // routing even though the backend's own stock numbers (ALLOC/RET/
          // OUT/STOCK, current_value) already account for it correctly.
          qty: Number(p.qty || 1),
          items: [p],
          item_ids: p.id ? [p.id] : [],
          serial_list: serial ? [serial] : [],
          sku_list: sku ? [sku] : [],
          total_allocated: Number(p.total_allocated || 0),
          total_returned: Number(p.total_returned || 0),
          currently_out: Number(p.currently_out || 0),
          current_value: Number(p.current_value || p.price || 0),
          holder_names: getHolderNamesFromRow(p),
        });
      } else {
        const item = map.get(key);

        item.qty += Number(p.qty || 1);
        item.items.push(p);

        if (p.id) item.item_ids.push(p.id);
        if (serial) item.serial_list.push(serial);
        if (sku) item.sku_list.push(sku);

        item.total_allocated += Number(p.total_allocated || 0);
        item.total_returned += Number(p.total_returned || 0);
        item.currently_out += Number(p.currently_out || 0);
        item.current_value += Number(p.current_value || p.price || 0);
        item.holder_names = [
          ...new Set([...(item.holder_names || []), ...getHolderNamesFromRow(p)]),
        ];
      }
    });

    return Array.from(map.values()).map((item) => {
      // `currently_out` already reflects units with an active ('ALLOCATED')
      // allocation right now. It must NOT be reduced by `total_returned`,
      // which is a lifetime count of past returns — those two numbers
      // measure different things and subtracting them understates how many
      // units are actually out, which overstates available stock for any
      // product that has ever been returned and later re-allocated.
      const netOut = Math.max(0, Number(item.currently_out || 0));
      const availableStock = Math.max(0, Number(item.qty || 0) - netOut);

      return {
        ...item,
        currently_out: netOut,
        available_stock: availableStock,
        current_holder_name_list: item.holder_names || [],
        current_holder_names: (item.holder_names || []).join(", "),
        sku: item.sku_list?.[0] || item.sku || "",
        serial_no:
          item.serial_list?.length > 1
            ? `${item.serial_list[0]} +${item.serial_list.length - 1} more`
            : item.serial_list?.[0] || item.serial_no || "",
        // Discard is per-item (is_discarded is deliberately NOT part of the
        // group_key above, so a group can legitimately mix discarded and
        // still-active items) -- a group only reads as "Discarded" once
        // EVERY item in it has been. This is the same rule the single-item
        // case (the common, items.length === 1 case) trivially satisfies.
        is_discarded: item.items.every((i) => !!i.is_discarded),
      };
    });
  }, [products]);

  const brandOptions = useMemo(() => {
    return [
      ...new Set(
        groupedProducts
          .map((p) => String(p.brand || "").trim())
          .filter(Boolean)
      ),
    ].sort((a, b) => a.localeCompare(b));
  }, [groupedProducts]);

  // Add/Edit Product dropdowns: every Product Name / Category / Brand already
  // used by the loaded products (CreatableTextSelect de-duplicates and sorts).
  const formValueOptions = useMemo(() => ({
    name: products.map((p) => p.name),
    category: products.map((p) => p.category),
    brand: products.map((p) => p.brand),
  }), [products]);

  const holderOptions = useMemo(() => {
    const names = new Set();

    groupedProducts.forEach((p) => {
      (p.current_holder_name_list || []).forEach((name) => {
        if (name) names.add(name);
      });
    });

    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [groupedProducts]);

  // Products' canonical reporting date is products.purchase_date (business
  // purchase date, already the field shown as "Purchase" and used in the
  // existing CSV export) — not created_at, which is only a row-insert audit
  // timestamp never surfaced anywhere in this feature.
  const monthOptions = useMemo(
    () => buildMonthOptions(groupedProducts, "purchase_date"),
    [groupedProducts]
  );

  /* ── filter ── */
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let data = [...groupedProducts];

    // "Discarded" is a permanent retirement, deliberately NOT part of the
    // products.status ACTIVE/INACTIVE enum -- filtered on the derived
    // is_discarded flag separately, same split as InventoryProductList.js's
    // own status filter.
    if (statusFilter === "DISCARDED") {
      data = data.filter((p) => p.is_discarded);
    } else if (statusFilter === "RETURNED") {
      // Not a products.status value: assets with at least one RETURNED
      // allocation (the RET column) -- same rule as the "Returned Qty"
      // card's returned-items list. Discarded assets stay under Discarded.
      data = data.filter((p) => Number(p.total_returned || 0) > 0 && !p.is_discarded);
    } else if (statusFilter !== "ALL") {
      data = data.filter((p) => p.status === statusFilter && !p.is_discarded);
    }

    if (brandFilter !== "ALL") {
      data = data.filter((p) => String(p.brand || "") === String(brandFilter));
    }

    if (holderFilter !== "ALL") {
      data = data.filter((p) =>
        (p.current_holder_name_list || []).some(
          (name) => String(name) === String(holderFilter)
        )
      );
    }

    if (monthFilter !== "ALL") {
      data = data.filter((p) => getMonthKey(p.purchase_date) === monthFilter);
    }

    if (q) {
      const fields = [
        "sku",
        "name",
        "category",
        "brand",
        "model_no",
        "serial_no",
        "processor",
        "unit_name",
        "warranty_status",
        "purchase_date",
        "warranty_expiry_date",
        "current_holder_names",
      ];

      // Production bug: searching an individual SKU/serial number returned
      // "No products found" even though the product genuinely exists,
      // Active, not discarded. Root cause -- `sku`/`serial_no` above are
      // only ever the GROUP's single representative item (whichever row
      // groupedProducts happened to encounter first -- see its own
      // group_key comment), never every physical item folded into a group.
      // A multi-item group's OTHER members' sku/serial were simply never
      // reachable by search at all. `name`/`model_no` don't need the same
      // fix -- they're part of group_key itself, so every member already
      // shares the identical value the representative fields above already
      // check. Reproduced/verified live: see task investigation for
      // SKU-000153 (a non-representative member of a "Wireless Mouse/M185"
      // group).
      data = data.filter((p) => {
        const matchesGroupFields = fields
          .map((f) => p[f])
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));

        if (matchesGroupFields) return true;

        const matchesAnyMemberSku = (p.sku_list || []).some((v) =>
          String(v).toLowerCase().includes(q)
        );
        const matchesAnyMemberSerial = (p.serial_list || []).some((v) =>
          String(v).toLowerCase().includes(q)
        );

        return matchesAnyMemberSku || matchesAnyMemberSerial;
      });

      // Task 4: "the containing group with the matching item clearly
      // identifiable" -- when the match came from a non-representative
      // member (the group's own displayed SKU/serial didn't match the
      // query), surface exactly which physical item matched so the
      // technician isn't left looking at a row whose visible SKU/serial
      // don't match what they typed.
      data = data.map((p) => {
        const repMatches =
          (p.sku && String(p.sku).toLowerCase().includes(q)) ||
          (p.serial_no && String(p.serial_no).toLowerCase().includes(q));

        if (repMatches) return p;

        const matchedItem = (p.items || []).find(
          (item) =>
            (item.sku && String(item.sku).toLowerCase().includes(q)) ||
            (item.serial_no && String(item.serial_no).toLowerCase().includes(q))
        );

        return matchedItem ? { ...p, matched_item: matchedItem } : p;
      });
    }

    return data;
  }, [groupedProducts, query, statusFilter, brandFilter, holderFilter, monthFilter]);

  // KPI cards: period-based totals (Total Products, Allocated Qty, Returned
  // Qty) are computed over the filtered (month-aware) product cohort, so
  // they reflect "of the products purchased in <month>, ...". Currently
  // Out / Available Stock / Current Value are still summed only over that
  // same filtered cohort, but the numbers themselves stay LIVE/current-state
  // (there is no historical allocations snapshot to reconstruct a past
  // month's stock state from) — see the note rendered next to the stats
  // row whenever a specific month is selected.
  const stats = useMemo(
    () => ({
      totalProducts: filtered.reduce((s, p) => s + Number(p.qty || 0), 0),
      totalAllocated: filtered.reduce(
        (s, p) => s + Number(p.total_allocated || 0),
        0
      ),
      totalReturned: filtered.reduce(
        (s, p) => s + Number(p.total_returned || 0),
        0
      ),
      currentlyOut: filtered.reduce(
        (s, p) => s + Number(p.currently_out || 0),
        0
      ),
      availableStock: filtered.reduce(
        (s, p) => s + Number(p.available_stock || 0),
        0
      ),
      currentValue: filtered.reduce(
        (s, p) => s + Number(p.current_value || 0),
        0
      ),
    }),
    [filtered]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageData = useMemo(
    () => filtered.slice((page - 1) * perPage, page * perPage),
    [filtered, page, perPage]
  );
  const returnedAllocHistoryRows = useMemo(
  () =>
    allocHistoryRows.filter(
      (row) => String(row.status || "").toUpperCase() === "RETURNED"
    ),
  [allocHistoryRows]
);

const currentAllocHistoryRows = useMemo(
  () =>
    allocHistoryRows.filter(
      (row) => String(row.status || "").toUpperCase() === "ALLOCATED"
    ),
  [allocHistoryRows]
);

// A physical item (source_product_id) can have MULTIPLE allocation history
// records over time (issued -> returned -> issued again). A past RETURNED
// record must not be shown as "In Stock" if that same item has since been
// re-allocated -- "current state" is a property of the ITEM right now, not
// of any one historical record. This maps each item to whether it is
// currently out and to whom, so every card/row for that item (including its
// older RETURNED records) reflects the item's actual current state.
const currentHolderByItemId = useMemo(() => {
  const map = new Map();
  currentAllocHistoryRows.forEach((row) => {
    if (row.source_product_id != null) {
      map.set(row.source_product_id, row.user_name || "Assigned User");
    }
  });
  return map;
}, [currentAllocHistoryRows]);

// "In Stock" for this popup is the backend's own canonical `available_qty`
// (see getAllocatioProductnHistory in productController.js) -- NOT
// `total_returned`, which is a lifetime count that stays the same even
// after a returned item is re-allocated (see currentHolderByItemId above
// for the same reasoning applied per-card). Never recalculated from
// returned-record counts in the frontend.
const historyAvailableStock = Number(allocHistorySummary?.available_qty || 0);

const visibleAllocHistoryRows = useMemo(
  () =>
    allocHistoryMode === "RETURNED"
      ? returnedAllocHistoryRows
      : allocHistoryRows,
  [allocHistoryMode, returnedAllocHistoryRows, allocHistoryRows]
);

const openAllReturnedHistory = async () => {
  const allItems = groupedProducts.flatMap((p) =>
    Array.isArray(p.items) ? p.items : [p]
  );

  const returnedItems = allItems.filter((item) =>
    Number(item.total_returned || 0) > 0
  );

  if (!returnedItems.length) {
    pop("No returned items found", "danger");
    return;
  }

  setAllocHistoryMode("RETURNED");
  setAllocHistoryProduct({
    name: selectedUnitInfo?.unit_name
      ? `${selectedUnitInfo.unit_name} - All Returned Products`
      : "All Returned Products",
    items: returnedItems,
  });

  setAllocHistoryRows([]);
  setAllocHistorySummary(null);
  setShowAllocHistory(true);

  await fetchAllocationHistoryForItems(returnedItems);
};

  /* ── add product ── */
  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setEditField = (k, v) => setEditForm((p) => ({ ...p, [k]: v }));

  const openAdd = () => {
    if (!hasConcreteUnitSelected) {
      pop("Please select a specific unit first", "danger");
      return;
    }

    setForm(BLANK);
    setShowAdd(true);
  };

  const handleAdd = async () => {
  if (!hasConcreteUnitSelected) {
    pop("Please select a specific unit", "danger");
    return;
  }

  if (!form.name.trim()) {
    pop("Product Name is required", "danger");
    return;
  }

  const serials = parseSerials(form.serial_numbers);

  if (!serials.length) {
    pop("Enter at least one serial number", "danger");
    return;
  }

  const qty = serials.length || Number(form.allocation_qty || form.qty || 1);

  if (!qty || qty <= 0) {
    pop("Qty must be > 0", "danger");
    return;
  }

  if (!isValidLifeValue(form.useful_life_value)) {
    pop("Asset Life must be a positive whole number", "danger");
    return;
  }
  const lifeConfigured = form.useful_life_value !== "" && form.useful_life_value != null;

  try {
    setSaving(true);

   const payload = {
  unit_id: Number(selectedUnit),
  name: form.name.trim(),
  category: form.category || "",
  brand: form.brand || "",
  model_no: form.model_no || "",
  serial_numbers: serials,

  processor: form.processor || "",
  price: Number(form.price || 0),
  purchase_date: form.purchase_date || null,
  warranty_date: form.warranty_date || form.purchase_date || null,
  warranty_expiry_date: form.warranty_expiry_date || null,

  qty: serials.length || Number(form.qty || qty),
  allocation_qty: serials.length || qty,

  ram_gb: form.ram_gb === "" ? null : Number(form.ram_gb),
  ssd_gb: form.ssd_gb === "" ? null : Number(form.ssd_gb),
  hdd_gb: form.hdd_gb === "" ? null : Number(form.hdd_gb),

  status: form.status || "ACTIVE",

  has_cpu: form.has_cpu ? 1 : 0,
  has_monitor: form.has_monitor ? 1 : 0,
  has_mouse: form.has_mouse ? 1 : 0,
  has_keyboard: form.has_keyboard ? 1 : 0,
  has_adapter: form.has_adapter ? 1 : 0,

  useful_life_value: lifeConfigured ? Number(form.useful_life_value) : null,
  useful_life_unit: lifeConfigured ? form.useful_life_unit || "YEARS" : null,

  notes: form.notes || "",
};

    const addRes = await api.post(
      `/products/unit-wise/${selectedUnit}/add`,
      payload
    );

    const createdProductIds = extractCreatedProductIds(addRes.data);

    if (hasInvoiceInput(form)) {
      if (!form.invoice_number) {
        pop(
          "Product added, but invoice not saved because invoice number is missing",
          "danger"
        );
      } else if (!createdProductIds.length) {
        pop(
          "Product added, but invoice not saved because created product id was not returned",
          "danger"
        );
      } else {
        await Promise.all(
          createdProductIds.map((productId) =>
            api.post(
              `/products/${productId}/vendor-invoices`,
              buildInvoiceFormData(form, {
                purchase_date: form.purchase_date,
                price: form.price,
              }),
              { headers: { "Content-Type": "multipart/form-data" } }
            )
          )
        );
      }
    }

    pop(
      serials.length > 1
        ? `${serials.length} products added successfully`
        : "Product added successfully"
    );

    setShowAdd(false);
    setForm(BLANK);

    await fetchUnitProducts(selectedUnit);
    await fetchUnits(false);
    await fetchVendorCompanies();
  } catch (e) {
    pop(e?.response?.data?.error || "Failed to add product", "danger");
  } finally {
    setSaving(false);
  }
};

  /* ── view/edit/delete ── */
  const openGroupView = (group) => {
    setViewGroup(group);
    setShowView(true);
  };

  const openDiscardPicker = (group) => {
    setDiscardPickerGroup(group);
    setShowDiscardPicker(true);
  };

  // Keeps the open "Select Product to Discard" popup showing live data
  // (item counts, Active/Discarded status per row) after a serial inside it
  // is discarded and the page refetches -- `discardPickerGroup` was captured
  // as a snapshot at open time, so without this it would keep showing the
  // pre-discard state even though `products`/`groupedProducts` moved on.
  useEffect(() => {
    if (!showDiscardPicker || !discardPickerGroup) return;

    const fresh = groupedProducts.find(
      (g) => g.group_key === discardPickerGroup.group_key
    );

    if (fresh && fresh !== discardPickerGroup) {
      setDiscardPickerGroup(fresh);
    }
    // Intentionally keyed on group_key (not the discardPickerGroup object
    // itself) -- the object reference always changes on refetch, which
    // would otherwise re-run this effect forever without ever converging.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedProducts, showDiscardPicker, discardPickerGroup?.group_key]);

  // Same live-data sync for the open Serial Items modal, so an item added
  // via Add Asset Item shows up (and Total Items updates) after the refetch
  // without closing the modal.
  useEffect(() => {
    if (!showView || !viewGroup) return;

    const fresh = groupedProducts.find((g) => g.group_key === viewGroup.group_key);

    if (fresh && fresh !== viewGroup) {
      setViewGroup(fresh);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedProducts, showView, viewGroup?.group_key]);

  /* ── Add Asset Item (to the open group) ──
     POST /products/:id/group-items copies every group-level field from the
     source item server-side (that is what keeps the new item in THIS
     group); only the item-specific fields below are sent. */
  const openAddItem = (group) => {
    const items = group?.items || [];
    const source = items.find((i) => !i.is_discarded) || items[0];
    if (!source) return;

    setAddItemSource({ ...source, unit_name: source.unit_name || group.unit_name || selectedUnitInfo?.unit_name });
    setAddItemForm({
      serial_no: "",
      warranty_date: inputDate(source.warranty_date || source.purchase_date),
      useful_life_value: source.useful_life_value ?? "",
      useful_life_unit: source.useful_life_unit || "YEARS",
      ...Object.fromEntries(ACCESSORIES.map(([k]) => [k, !!source[k]])),
    });
    setAddItemError("");
  };

  const closeAddItem = () => {
    if (addingItem) return;
    setAddItemSource(null);
    setAddItemForm(null);
    setAddItemError("");
  };

  const setAddItemField = (key, value) => {
    setAddItemForm((p) => ({ ...p, [key]: value }));
    setAddItemError("");
  };

  const handleAddItemSave = async () => {
    if (!addItemSource || !addItemForm) return;

    const serialNo = String(addItemForm.serial_no || "").trim();
    if (!serialNo) {
      setAddItemError("Serial number is required.");
      return;
    }
    // Quick check against what is loaded; the backend checks every unit.
    if (products.some((p) => String(p.serial_no || "").trim().toLowerCase() === serialNo.toLowerCase())) {
      setAddItemError("Serial number already exists");
      return;
    }
    if (!isValidLifeValue(addItemForm.useful_life_value)) {
      setAddItemError("Asset Life must be a positive whole number.");
      return;
    }

    const hasLife = addItemForm.useful_life_value !== "" && addItemForm.useful_life_value != null;
    const payload = {
      serial_no: serialNo,
      warranty_date: addItemForm.warranty_date || null,
      useful_life_value: hasLife ? Number(addItemForm.useful_life_value) : null,
      useful_life_unit: hasLife ? addItemForm.useful_life_unit || "YEARS" : null,
      ...Object.fromEntries(ACCESSORIES.map(([k]) => [k, !!addItemForm[k]])),
    };

    try {
      setAddingItem(true);
      await api.post(`/products/${addItemSource.id}/group-items`, payload);

      setAddItemSource(null);
      setAddItemForm(null);
      pop(`Asset item ${serialNo} added`);

      // Refetch; the effect above re-binds the open Serial Items modal.
      if (selectedUnit === ALL_UNITS) await fetchAllProducts();
      else if (selectedUnit) await fetchUnitProducts(selectedUnit);
      await fetchUnits(false);
    } catch (e) {
      setAddItemError(e?.response?.data?.error || e?.response?.data?.message || "Failed to add asset item");
    } finally {
      setAddingItem(false);
    }
  };

  const openEditSingle = (product) => {
    setEditMode("single");
    setEditTarget(product);
    setEditForm(productToForm(product));
    setEditQtyError("");
    setShowEdit(true);
  };

  const openEditGroup = (group) => {
    setEditMode("group");
    setEditTarget(group);
    setEditForm(productToForm(group));
    setEditQtyError("");
    setShowEdit(true);
  };

  // keepSerial doubles as "is this a group-edit call" (true for each item in
  // a multi-row serialized group, false for a plain single-row edit) -- see
  // the two call sites in handleEditSave below. Quantity must follow the
  // same split: a group edit shares ONE editForm across every serialized
  // row in that group, so trusting formData.qty there would overwrite every
  // row's own qty (normally 1 each) with the group's total item count. Only
  // a genuine single-row edit (keepSerial=false) may apply the user's typed
  // Total Qty -- previously this always kept baseProduct.qty regardless of
  // what was typed, silently discarding any quantity edit.
  const buildUpdatePayload = (baseProduct, formData, keepSerial = true) => ({
    name: formData.name || "",
    category: formData.category || "",
    brand: formData.brand || "",
    price: Number(formData.price || 0),
    qty: keepSerial
      ? Number(baseProduct?.qty || 1)
      : Number(formData.qty || baseProduct?.qty || 1),
    status: formData.status || "ACTIVE",

    purchase_date: formData.purchase_date || null,
    warranty_date: formData.warranty_date || formData.purchase_date || null,
    warranty_expiry_date: formData.warranty_expiry_date || null,

    model_no: formData.model_no || "",
    serial_no: keepSerial
      ? baseProduct?.serial_no || ""
      : formData.serial_numbers || "",
    processor: formData.processor || "",
    ram_gb: formData.ram_gb === "" ? null : Number(formData.ram_gb),
    ssd_gb: formData.ssd_gb === "" ? null : Number(formData.ssd_gb),
    hdd_gb: formData.hdd_gb === "" ? null : Number(formData.hdd_gb),

    has_cpu: formData.has_cpu ? 1 : 0,
    has_monitor: formData.has_monitor ? 1 : 0,
    has_mouse: formData.has_mouse ? 1 : 0,
    has_keyboard: formData.has_keyboard ? 1 : 0,
    has_adapter: formData.has_adapter ? 1 : 0,

    // Asset Life is per item: only a single-item edit sends it (the backend
    // writes it only when the keys are present). Group edit never does, so
    // each serial keeps its own life.
    ...(keepSerial
      ? {}
      : {
          useful_life_value:
            formData.useful_life_value === "" || formData.useful_life_value == null
              ? null
              : Number(formData.useful_life_value),
          useful_life_unit:
            formData.useful_life_value === "" || formData.useful_life_value == null
              ? null
              : formData.useful_life_unit || "YEARS",
        }),
  });

  const handleEditSave = async () => {
    if (!editTarget) return;

    if (!editForm.name.trim()) {
      pop("Product name is required", "danger");
      return;
    }

    if (hasInvoiceInput(editForm) && !editForm.invoice_number) {
      pop("Invoice number is required to save invoice", "danger");
      return;
    }

    // Inventory-integrity rule: only a genuine single bulk row exposes an
    // editable Total Qty at all (see the field's own visibility condition
    // below) -- group mode never sends editForm.qty (buildUpdatePayload
    // keeps each item's own qty), so this check is scoped to single mode
    // only, matching exactly what the user could have changed.
    if (editMode === "single" && !isValidLifeValue(editForm.useful_life_value)) {
      pop("Asset Life must be a positive whole number", "danger");
      return;
    }

    if (editMode === "single") {
      const qtyMessage = validateEditQty(editForm.qty, editTarget?.currently_out);
      if (qtyMessage) {
        setEditQtyError(qtyMessage);
        pop(qtyMessage, "danger");
        return;
      }
    }
    setEditQtyError("");

    try {
      setSaving(true);

      if (editMode === "group") {
        // A group can mix discarded and active items (is_discarded is not
        // part of group_key). Discarded items are read-only -- the backend
        // rejects them with 409 PRODUCT_DISCARDED_READ_ONLY -- so they are
        // skipped here instead of failing the whole group edit.
        const allItems = Array.isArray(editTarget.items) ? editTarget.items : [];
        const items = allItems.filter((item) => item.id && !item.is_discarded);
        const skippedDiscarded = allItems.length - items.length;

        if (!items.length) {
          pop(
            allItems.length
              ? "All items in this group are discarded and cannot be edited"
              : "No product items found for this group",
            "danger"
          );
          return;
        }

        // One atomic request (PUT /products/group-edit): the backend checks
        // every item (exists, caller's unit, not discarded) and updates all
        // of them or none -- never a partial group. Serial no / SKU / qty
        // are per-item and are not sent at all.
        const { serial_no, qty, ...groupFields } = buildUpdatePayload(
          editTarget,
          editForm,
          true
        );
        try {
          await api.put("/products/group-edit", {
            ids: items.map((item) => item.id),
            fields: groupFields,
          });
        } catch (err) {
          pop(
            err?.response?.data?.error ||
              err?.response?.data?.message ||
              "Failed to update asset group. No items were changed.",
            "danger"
          );
          return;
        }

        const skippedNote = skippedDiscarded
          ? ` (${skippedDiscarded} discarded item(s) skipped)`
          : "";

        // Invoices are separate per-item requests (multipart file upload,
        // not part of the group-edit transaction). The group fields above
        // are already saved at this point, so a failed invoice is reported
        // as exactly that -- never as "update failed".
        if (hasInvoiceInput(editForm)) {
          const invoiceResults = await Promise.allSettled(
            items.map((item) =>
              api.post(
                `/products/${item.id}/vendor-invoices`,
                buildInvoiceFormData(editForm, {
                  purchase_date: editForm.purchase_date || item.purchase_date,
                  price: editForm.price || item.price,
                }),
                { headers: { "Content-Type": "multipart/form-data" } }
              )
            )
          );
          const failedInvoices = items
            .map((item, i) => ({ item, result: invoiceResults[i] }))
            .filter(({ result }) => result.status === "rejected");

          if (failedInvoices.length) {
            const reasons = failedInvoices
              .map(({ item, result }) =>
                `${item.serial_no || item.sku || `#${item.id}`}: ${
                  result.reason?.response?.data?.error ||
                  result.reason?.response?.data?.message ||
                  result.reason?.message ||
                  "invoice failed"
                }`
              )
              .join("; ");
            pop(
              `${items.length} asset item(s) updated${skippedNote}. Invoice added to ${
                items.length - failedInvoices.length
              } of ${items.length} -- failed: ${reasons}`,
              "danger"
            );
            setShowEdit(false);
            setEditTarget(null);
            setEditForm({ ...BLANK });
            await fetchUnitProducts(selectedUnit);
            await fetchUnits(false);
            return;
          }
        }

        pop(
          hasInvoiceInput(editForm)
            ? `${items.length} asset item(s) updated and invoice added successfully${skippedNote}`
            : `${items.length} asset item(s) updated successfully${skippedNote}`
        );
      } else {
        await api.put(
          `/products/${editTarget.id}`,
          buildUpdatePayload(editTarget, editForm, false)
        );

        if (hasInvoiceInput(editForm)) {
          await api.post(
            `/products/${editTarget.id}/vendor-invoices`,
            buildInvoiceFormData(editForm, {
              purchase_date: editForm.purchase_date || editTarget.purchase_date,
              price: editForm.price || editTarget.price,
            }),
            { headers: { "Content-Type": "multipart/form-data" } }
          );
        }

        pop(
          hasInvoiceInput(editForm)
            ? "Product updated and invoice added successfully"
            : "Product updated successfully"
        );
      }

      setShowEdit(false);
      setEditTarget(null);
      setEditForm({ ...BLANK });

      await fetchUnitProducts(selectedUnit);
      await fetchUnits(false);
    } catch (e) {
      pop(e?.response?.data?.error || "Failed to update product", "danger");
    } finally {
      setSaving(false);
    }
  };

  const inactiveProducts = async (items, label = "product") => {
    if (!items?.length) return;

    const ok = window.confirm(
      `Are you sure you want to delete/inactive ${items.length} ${label}?`
    );

    if (!ok) return;

    try {
      setDeleting(true);

      await Promise.all(
        items.map((item) =>
          api.put(`/products/${item.id}/status`, {
            status: "INACTIVE",
          })
        )
      );

      pop(`${items.length} ${label} inactive successfully`);

      setShowView(false);

      await fetchUnitProducts(selectedUnit);
      await fetchUnits(false);
    } catch (e) {
      pop(e?.response?.data?.error || "Failed to inactive product", "danger");
    } finally {
      setDeleting(false);
    }
  };

  /* ── manual discard (single item only -- reuses the existing, working
     PUT /products/:id/discard endpoint; never touched here) ── */
  const openDiscardModal = (item) => {
    setDiscardTarget(item);
    setDiscardForm({ discard_date: todayInputDate(), reason: "", remarks: "", ...BLANK_DISPOSAL });
  };

  const submitDiscard = async () => {
    if (!discardTarget) return;

    if (!discardForm.discard_date) {
      pop("Discard Date is required", "danger");
      return;
    }
    if (!discardForm.reason) {
      pop("Reason is required", "danger");
      return;
    }
    if (discardForm.reason === "OTHER" && !discardForm.remarks.trim()) {
      pop('Remarks are required when Reason is "Other"', "danger");
      return;
    }
    const disposalError = validateDisposalForm(discardForm, { purchaseDate: discardTarget.purchase_date });
    if (disposalError) {
      pop(disposalError, "danger");
      return;
    }

    try {
      setDiscarding(true);

      await api.put(`/products/${discardTarget.id}/discard`, {
        discard_date: discardForm.discard_date,
        reason: discardForm.reason,
        remarks: discardForm.remarks || null,
        ...disposalPayload(discardForm),
      });

      pop("Product discarded successfully");
      setDiscardTarget(null);

      // UX: without this, a product discarded while viewing the (default)
      // "Active" filter would correctly stop matching it and vanish from
      // the list the instant the refetch below completes -- indistinguishable
      // from having been deleted. Switching to "Discarded" keeps the exact
      // same row visible, now showing its new Discarded badge, so the user
      // sees the action succeed rather than wondering if the row disappeared.
      // The filter itself is unchanged -- Active/Inactive/All still work
      // exactly as before; this only changes which one is SELECTED right
      // after a successful discard.
      //
      // ONLY when this discard makes the whole GROUP discarded (the group's
      // own is_discarded is items.every(is_discarded) -- see groupedProducts)
      // does switching filters keep that same row visible. For a
      // multi-quantity group where other items are still active, the group
      // stays visible under its CURRENT filter regardless (is_discarded is
      // still false), so forcing "Discarded" here would instead make it
      // vanish from view -- exactly the "looks deleted" outcome the
      // serial-wise discard flow must avoid.
      const siblingGroup = groupedProducts.find((g) =>
        (g.items || []).some((i) => i.id === discardTarget.id)
      );
      const groupNowFullyDiscarded =
        !siblingGroup ||
        (siblingGroup.items || [])
          .filter((i) => i.id !== discardTarget.id)
          .every((i) => i.is_discarded);

      if (groupNowFullyDiscarded) {
        setStatusFilter("DISCARDED");
      }

      // NOT fetchUnitProducts(selectedUnit) directly -- selectedUnit is
      // "ALL" by default (this page's own "no unit filter" sentinel), which
      // is not a real unit id; calling fetchUnitProducts with it hits
      // GET /products/unit-wise/ALL and the backend correctly rejects it
      // ("Invalid unit id"), leaving `products` silently stale. handleRefresh
      // (already used by this page's own Refresh button) is the existing,
      // correct dispatcher that already routes ALL vs a real unit id right.
      await handleRefresh();
    } catch (e) {
      // PUT /products/:id/discard returns the ApiError shape
      // ({success:false, code, message}), unlike this page's older
      // status/save endpoints (which return {error: "..."}) -- read both,
      // same fix InventoryProductList.js's own submitDiscard needed.
      pop(e?.response?.data?.message || e?.response?.data?.error || "Failed to discard product", "danger");
    } finally {
      setDiscarding(false);
    }
  };

  /* ── product invoices ── */
  const fetchInvoicesForItems = async (items = []) => {
    const ids = items.map((item) => item.id).filter(Boolean);

    if (!ids.length) {
      setInvoiceRows([]);
      setPreviewInvoice(null);
      return;
    }

    setLoadingInvoices(true);

    try {
      const responses = await Promise.all(
        ids.map((productId) =>
          api
            .get(`/products/${productId}/vendor-invoices`)
            .then((res) => ({ productId, data: res.data }))
            .catch(() => ({ productId, data: [] }))
        )
      );

      const rows = responses.flatMap((r) =>
        (Array.isArray(r.data) ? r.data : []).map((invoice) => ({
          ...invoice,
          source_product_id: r.productId,
        }))
      );

      setInvoiceRows(rows);
      setPreviewInvoice(rows[0] || null);
    } catch (e) {
      pop(e?.response?.data?.error || "Failed to fetch product invoices", "danger");
    } finally {
      setLoadingInvoices(false);
    }
  };

  const openInvoices = async (groupOrItem) => {
    const items = Array.isArray(groupOrItem?.items)
      ? groupOrItem.items
      : [groupOrItem];

    setInvoiceProduct({ ...groupOrItem, items });
    setInvoiceRows([]);
    setPreviewInvoice(null);
    setInvoiceForm({ ...BLANK });
    setShowInvoiceCreate(false);
    setShowInvoices(true);

    await fetchInvoicesForItems(items);
  };

  const setInvoiceField = (k, v) =>
    setInvoiceForm((p) => ({ ...p, [k]: v }));

  const handleInvoiceCreate = async () => {
    const items = Array.isArray(invoiceProduct?.items)
      ? invoiceProduct.items
      : invoiceProduct
      ? [invoiceProduct]
      : [];

    const ids = items.map((item) => item.id).filter(Boolean);

    if (!ids.length) {
      pop("Product id not found", "danger");
      return;
    }

    if (!invoiceForm.invoice_number) {
      pop("Invoice number is required", "danger");
      return;
    }

    try {
      setSavingInvoice(true);

      await Promise.all(
        ids.map((productId) =>
          api.post(
            `/products/${productId}/vendor-invoices`,
            buildInvoiceFormData(invoiceForm, {
              purchase_date: invoiceProduct?.purchase_date,
              price: invoiceProduct?.price,
            }),
            { headers: { "Content-Type": "multipart/form-data" } }
          )
        )
      );

      pop("Invoice uploaded successfully");
      setInvoiceForm({ ...BLANK });
      setShowInvoiceCreate(false);
      await fetchInvoicesForItems(items);
      await fetchVendorCompanies();
    } catch (e) {
      pop(e?.response?.data?.error || "Failed to upload invoice", "danger");
    } finally {
      setSavingInvoice(false);
    }
  };

  /* ── export CSV ── */
  const exportCsv = () => {
    if (!filtered.length) {
      pop("No data to export", "danger");
      return;
    }

    const headers = [
      "unit_name",
      "sku",
      "name",
      "category",
      "brand",
      "model_no",
      "serial_no",
      "processor",
      "ram_gb",
      "ssd_gb",
      "hdd_gb",
      "price",
      "purchase_date",
      "warranty_date",
      "warranty_expiry_date",
      "warranty_status",
      "total_allocated",
      "total_returned",
      "currently_out",
      "available_stock",
      "current_holder_names",
      "current_value",
      "status",
    ];

    const esc = (v) => (v == null ? "" : csvQuote(v));

    const lines = [
      headers.join(","),
      ...filtered.map((r) => headers.map((h) => esc(r[h])).join(",")),
    ];

    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8",
    });

    const unitPart = sanitizeFileNamePart(selectedUnitInfo?.unit_name);
    const monthPart = monthFileSuffix(monthFilter);

    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `technician-products-${unitPart}-${monthPart}.csv`;
    a.click();

    URL.revokeObjectURL(a.href);
  };

  const serialCount = parseSerials(form.serial_numbers).length;

  const ACCESSORIES = [
    ["has_cpu", "CPU"],
    ["has_monitor", "Monitor"],
    ["has_mouse", "Mouse"],
    ["has_keyboard", "Keyboard"],
    ["has_adapter", "Adapter"],
  ];

  const renderInvoiceFields = (currentForm, setter, options = {}) => (
    <div className="span3 puws-invoice-box">
      <div className="puws-invoice-box-head">
        <div>
          <strong>{options.title || "Purchase Invoice"}</strong>
          <p>
            {options.description ||
              "Optional: invoice details will be saved in product vendor invoice table."}
          </p>
        </div>
      </div>

      <div className="puws-form-grid compact">
        <div>
          <label className="puws-label">Invoice No.</label>
          <input
            className="puws-input"
            placeholder="INV-1001"
            value={currentForm.invoice_number || ""}
            onChange={(e) => setter("invoice_number", e.target.value)}
          />
        </div>

        <div className="span2" style={{ position: "relative" }}>
          <label className="puws-label">Vendor Company</label>
          <input
            className="puws-input"
            placeholder="Type 1-2 letters to search vendor company"
            value={currentForm.vendor_company_name || ""}
            onFocus={() => setShowVendorDrop(true)}
            onBlur={() => setTimeout(() => setShowVendorDrop(false), 150)}
            onChange={(e) => {
              setter("vendor_company_name", e.target.value);
              setter("vendor_email", "");
              setter("vendor_phone", "");
              setShowVendorDrop(true);
            }}
          />

          {showVendorDrop && currentForm.vendor_company_name && (
            <div
              style={{
                position: "absolute",
                top: "72px",
                left: 0,
                right: 0,
                background: "#ffffff",
                border: "1px solid #e5e7eb",
                borderRadius: "10px",
                boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
                zIndex: 9999,
                maxHeight: "220px",
                overflowY: "auto",
              }}
            >
              {vendorCompanies
                .filter((c) =>
                  String(c.name || "")
                    .toLowerCase()
                    .includes(
                      String(currentForm.vendor_company_name || "").toLowerCase()
                    )
                )
                .slice(0, 10)
                .map((c) => (
                  <div
                    key={c.id}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setter("vendor_company_name", c.name || "");
                      setter("vendor_email", c.email || "");
                      setter("vendor_phone", c.phone_number || "");
                      setShowVendorDrop(false);
                    }}
                    style={{
                      padding: "10px 12px",
                      cursor: "pointer",
                      borderBottom: "1px solid #f3f4f6",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#111827",
                      background: "#ffffff",
                    }}
                  >
                    <div>{c.name}</div>
                    {(c.email || c.phone_number) && (
                      <small style={{ color: "#6b7280", fontWeight: 500 }}>
                        {c.email || "-"}
                        {c.phone_number ? ` | ${c.phone_number}` : ""}
                      </small>
                    )}
                  </div>
                ))}

              {vendorCompanies.filter((c) =>
                String(c.name || "")
                  .toLowerCase()
                  .includes(
                    String(currentForm.vendor_company_name || "").toLowerCase()
                  )
              ).length === 0 && (
                <div
                  style={{
                    padding: "10px 12px",
                    color: "#6b7280",
                    fontSize: "13px",
                    background: "#ffffff",
                  }}
                >
                  No existing vendor found. This will be saved as a new vendor name.
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          <label className="puws-label">Vendor Email</label>
          <input
            type="email"
            className="puws-input"
            placeholder="vendor@example.com"
            value={currentForm.vendor_email || ""}
            onChange={(e) => setter("vendor_email", e.target.value)}
          />
        </div>

        <div>
          <label className="puws-label">Vendor Phone</label>
          <input
            className="puws-input"
            placeholder="Vendor phone"
            value={currentForm.vendor_phone || ""}
            onChange={(e) => setter("vendor_phone", e.target.value)}
          />
        </div>

              <div>
        <label className="puws-label">Purchase Company</label>
        <select
          className="puws-input"
          value={currentForm.purchase_company_id || ""}
          onChange={(e) => {
            const companyId = e.target.value;
            const selected = companies.find(
              (c) => String(c.id) === String(companyId)
            );

            setter("purchase_company_id", companyId);
            setter("purchase_company_name", selected?.name || "");
          }}
        >
          <option value="">Select Purchase Company</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

        <div>
          <label className="puws-label">Invoice Date</label>
          <input
            type="date"
            className="puws-input"
            value={currentForm.invoice_purchase_date || ""}
            onChange={(e) => setter("invoice_purchase_date", e.target.value)}
          />
        </div>

        <div>
          <label className="puws-label">Invoice Amount</label>
          <input
            type="number"
            min="0"
            className="puws-input"
            value={currentForm.invoice_amount || ""}
            onChange={(e) => setter("invoice_amount", e.target.value)}
          />
        </div>

        <div>
          <label className="puws-label">Invoice File</label>
          <input
            type="file"
            className="puws-input"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.xls,.xlsx"
            onChange={(e) => {
              const file = e.target.files?.[0] || null;

              if (file && file.size > MAX_INVOICE_FILE_SIZE) {
                pop("Invoice file size must be 5 MB or less", "danger");
                e.target.value = "";
                setter("invoice_file", null);
                return;
              }

              setter("invoice_file", file);
            }}
          />
        </div>

        <div className="span3">
          <label className="puws-label">Invoice Remarks</label>
          <input
            className="puws-input"
            placeholder="Optional remarks"
            value={currentForm.invoice_remarks || ""}
            onChange={(e) => setter("invoice_remarks", e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  const renderProductForm = (currentForm, setter, isEdit = false) => {
    const serialCountInForm = parseSerials(currentForm.serial_numbers).length;

    return (
      <div className="puws-form-grid">
        <div className="span2">
          <label className="puws-label">
            Product Name<span className="req">*</span>
          </label>
          {/* Existing values from the loaded products; a new one can be typed. */}
          <CreatableTextSelect
            placeholder="Product name"
            options={formValueOptions.name}
            value={currentForm.name}
            onChange={(v) => setter("name", v)}
          />
        </div>

        <div>
          <label className="puws-label">Category</label>
          <CreatableTextSelect
            placeholder="Category"
            options={formValueOptions.category}
            value={currentForm.category}
            onChange={(v) => setter("category", v)}
          />
        </div>

        <div>
          <label className="puws-label">Brand</label>
          <CreatableTextSelect
            placeholder="Brand"
            options={formValueOptions.brand}
            value={currentForm.brand}
            onChange={(v) => setter("brand", v)}
          />
        </div>

        <div>
          <label className="puws-label">Model No.</label>
          <input
            className="puws-input"
            placeholder="Model No."
            value={currentForm.model_no}
            onChange={(e) => setter("model_no", e.target.value)}
          />
        </div>

        <div>
          <label className="puws-label">Status</label>
          <select
            className="puws-form-select"
            value={currentForm.status}
            onChange={(e) => setter("status", e.target.value)}
          >
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
        </div>

        {!isEdit && (
          <div className="span3">
            <label className="puws-label">
              Serial Numbers<span className="req">*</span>
              {serialCountInForm > 0 && (
                <span className="puws-serial-cnt">
                  {serialCountInForm} item{serialCountInForm > 1 ? "s" : ""}
                </span>
              )}
            </label>
            <textarea
              className="puws-textarea"
              rows={2}
              placeholder="SN001, SN002 or one per line"
              value={currentForm.serial_numbers}
              onChange={(e) => {
                const v = e.target.value;
                const c = parseSerials(v).length;

                setForm((p) => ({
                  ...p,
                  serial_numbers: v,
                  qty: c || 1,
                  allocation_qty: c || 1,
                }));
              }}
            />
          </div>
        )}

        {isEdit && editMode === "single" && (
          <div className="span3">
            <label className="puws-label">Serial No.</label>
            <input
              className="puws-input"
              value={currentForm.serial_numbers}
              onChange={(e) => setter("serial_numbers", e.target.value)}
            />
            {currentForm.serial_numbers && (
              <div className="puws-sku-note">
                Each physical unit has its own SKU. Add another unit instead of changing quantity.
              </div>
            )}
          </div>
        )}

        {isEdit && editMode === "group" && (
          <div className="span3">
            <div className="puws-unit-info">
              <div>
                <strong>Group Edit:</strong>{" "}
                {(editTarget?.items || []).filter((i) => !i.is_discarded).length}{" "}
                serial item(s) will be updated.
                {(editTarget?.items || []).some((i) => i.is_discarded) && (
                  <>
                    {" "}
                    {(editTarget?.items || []).filter((i) => i.is_discarded).length} discarded
                    item(s) will be skipped.
                  </>
                )}
              </div>
              <div className="puws-sku-note">
                Serial numbers will remain unchanged.
              </div>
            </div>
          </div>
        )}

        <div>
          <label className="puws-label">Purchase Date</label>
          <input
            type="date"
            className="puws-input"
            value={currentForm.purchase_date}
            onChange={(e) => setter("purchase_date", e.target.value)}
          />
        </div>

        <div>
          <label className="puws-label">Warranty Start</label>
          <input
            type="date"
            className="puws-input"
            value={currentForm.warranty_date}
            onChange={(e) => setter("warranty_date", e.target.value)}
          />
        </div>

        <div>
          <label className="puws-label">Warranty Expiry</label>
          <input
            type="date"
            className="puws-input"
            value={currentForm.warranty_expiry_date}
            onChange={(e) => setter("warranty_expiry_date", e.target.value)}
          />
        </div>

        {isEdit && editMode === "group" ? (
          <div className="span3">
            <div className="puws-sku-note">
              Asset Life / End Life is kept per item and is not changed by Group Edit. Edit an
              individual serial item to change it.
            </div>
          </div>
        ) : (
          <>
            <div>
              <label className="puws-label" htmlFor="puws-asset-life-value">Asset Life</label>
              <input
                id="puws-asset-life-value"
                type="number"
                min="1"
                step="1"
                className="puws-input"
                placeholder="e.g. 5 (optional)"
                value={currentForm.useful_life_value ?? ""}
                onChange={(e) => setter("useful_life_value", e.target.value)}
              />
            </div>

            <div>
              <label className="puws-label" htmlFor="puws-asset-life-unit">Asset Life Unit</label>
              <select
                id="puws-asset-life-unit"
                className="puws-input"
                value={currentForm.useful_life_unit || "YEARS"}
                onChange={(e) => setter("useful_life_unit", e.target.value)}
              >
                {LIFE_UNITS.map((u) => (
                  <option key={u.value} value={u.value}>{u.label}</option>
                ))}
              </select>
            </div>

            <div>
              <span className="puws-label">End Life Date</span>
              <div className="puws-sku-note" data-testid="puws-end-life-preview">
                {(() => {
                  const end = endLifeFormState({
                    purchaseDate: currentForm.purchase_date,
                    lifeValue: currentForm.useful_life_value,
                    lifeUnit: currentForm.useful_life_unit,
                    overrideEndDate: isEdit ? editTarget?.expected_life_override_end_date : null,
                  });
                  if (!end.date) return end.hint;
                  return end.overridden
                    ? `${fmtDateOnly(end.date)} (revised by Life Review)`
                    : fmtDateOnly(end.date);
                })()}
              </div>
            </div>

            {isEdit && (
              <div>
                <span className="puws-label">Life Status</span>
                <div data-testid="puws-edit-lifecycle">
                  <LifecycleBadge lifecycle={editTarget?.lifecycle} />
                </div>
              </div>
            )}
          </>
        )}

        <div className="span2">
          <label className="puws-label">Processor</label>
          <input
            className="puws-input"
            placeholder="e.g. Intel Core i5"
            value={currentForm.processor}
            onChange={(e) => setter("processor", e.target.value)}
          />
        </div>

        <div>
          <label className="puws-label">Price (₹)</label>
          <input
            type="number"
            min="0"
            className="puws-input"
            value={currentForm.price}
            onChange={(e) => setter("price", e.target.value)}
          />
        </div>

        <div>
          <label className="puws-label">RAM (GB)</label>
          <input
            type="number"
            min="0"
            className="puws-input"
            value={currentForm.ram_gb}
            onChange={(e) => setter("ram_gb", e.target.value)}
          />
        </div>

        <div>
          <label className="puws-label">SSD (GB)</label>
          <input
            type="number"
            min="0"
            className="puws-input"
            value={currentForm.ssd_gb}
            onChange={(e) => setter("ssd_gb", e.target.value)}
          />
        </div>

        <div>
          <label className="puws-label">HDD (GB)</label>
          <input
            type="number"
            min="0"
            className="puws-input"
            value={currentForm.hdd_gb}
            onChange={(e) => setter("hdd_gb", e.target.value)}
          />
        </div>

        {/* Total Qty: editable whenever this row has no serial number --
            add flow (no serial typed yet) OR editing an existing bulk/
            non-serialized row (single mode, serial_numbers empty). Hidden
            for "group" edit and for a single row that DOES have a serial,
            since each serialized unit is its own DB row/SKU -- changing
            "qty" there would need to add/remove actual rows, not just a
            number, and is out of scope here. Previously this was hidden
            for EVERY edit unconditionally, so a plain bulk item's quantity
            could never be corrected once created. */}
        {(!isEdit || (editMode === "single" && !currentForm.serial_numbers)) && (
          <div>
            <label className="puws-label">Total Qty</label>
            <input
              type="number"
              min={isEdit ? Math.max(1, Number(editTarget?.currently_out || 0)) : "1"}
              className="puws-input"
              value={currentForm.qty}
              disabled={!isEdit && serialCountInForm > 0}
              onChange={(e) => {
                setter("qty", e.target.value);
                if (!isEdit) {
                  setter("allocation_qty", e.target.value);
                } else {
                  // Live feedback as the user types -- the authoritative
                  // gate is still handleEditSave (which never sends the PUT
                  // while this is set) and the backend's own re-check;
                  // `min` on its own is a browser hint only, never trusted.
                  setEditQtyError(validateEditQty(e.target.value, editTarget?.currently_out));
                }
              }}
            />
            {isEdit && editQtyError && (
              <div className="puws-field-error">{editQtyError}</div>
            )}
            {isEdit && !editQtyError && (
              <div className="puws-sku-note">
                SKU identifies this bulk inventory record. Changing quantity does not create a new SKU.
              </div>
            )}
          </div>
        )}

        {!isEdit && (
          <>
            <div className="span2">
              <label className="puws-label">Qty for this Unit</label>
              <input
                type="number"
                min="1"
                className="puws-input"
                value={currentForm.allocation_qty}
                disabled={serialCountInForm > 0}
                onChange={(e) => setter("allocation_qty", e.target.value)}
              />
            </div>

            <div className="span3">
              <label className="puws-label">Notes</label>
              <input
                className="puws-input"
                placeholder="Optional notes"
                value={currentForm.notes}
                onChange={(e) => setter("notes", e.target.value)}
              />
            </div>
          </>
        )}

        <div className="span3">
          <label className="puws-label">Accessories</label>
          <div className="puws-checks">
            {ACCESSORIES.map(([k, lbl]) => (
              <label
                key={k}
                className={`puws-check-item${currentForm[k] ? " checked" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={!!currentForm[k]}
                  onChange={(e) => setter(k, e.target.checked)}
                />
                {currentForm[k] ? "✓ " : ""}
                {lbl}
              </label>
            ))}
          </div>
        </div>

        {/* Attaching an invoice always POSTs to .../vendor-invoices, which the
            backend gates on products:create specifically (routes/
            productVendorInvoiceRoutes.js) -- regardless of whether this form
            is in "add" or "edit" mode. A user with products:edit but not
            products:create must not be shown a form that will only ever
            403 on save. */}
        {!isEdit && canCreate && renderInvoiceFields(currentForm, setter)}

        {isEdit && canCreate &&
          renderInvoiceFields(currentForm, setter, {
            title: "Add Product Invoice",
            description:
              editMode === "group"
                ? "Optional: invoice will be attached to all serial items in this group."
                : "Optional: invoice will be attached to this product while updating.",
          })}
      </div>
    );
  };

  /* ════ RENDER ════ */
  return (
    <div className="puws-page puw-detail-theme tech-ds">
      {/* ── Header ──────────────────────────────── */}
      <div className="puws-header tech-page-header">
        <div>
          <div className="puws-breadcrumb tech-section-label">Technician Management</div>
          <h1 className="puws-title tech-page-title">
            Product <span>Inventory</span>
          </h1>
          <p className="puws-subtitle tech-page-subtitle">
            View inventory for each of your units.
          </p>
        </div>

        <div className="puws-header-actions tech-header-actions">
          <button
            className="puws-btn puws-btn-ghost"
            onClick={handleRefresh}
            disabled={loadingUnits || loadingProducts}
          >
            {loadingUnits || loadingProducts ? (
              <>
                <Spin /> Refreshing…
              </>
            ) : (
              <>
                <i className="fa-solid fa-rotate" aria-hidden="true"></i>
                Refresh
              </>
            )}
          </button>

          <button className="puws-btn puws-btn-ghost" onClick={exportCsv}>
            <i className="fa-solid fa-file-export" aria-hidden="true"></i>
            Export CSV
          </button>

          {canCreate && (
            <button
              className="puws-btn puws-btn-primary"
              onClick={openAdd}
              disabled={!hasConcreteUnitSelected}
              title={!hasConcreteUnitSelected ? "Select a specific unit to add a product" : ""}
            >
              <i className="fa-solid fa-plus" aria-hidden="true"></i>
              Add Product
            </button>
          )}
        </div>
      </div>

      {/* ── Unit Tabs ────────────────────────────── */}
      {units.length > 0 && (
        <div className="puws-unit-tabs tech-unit-tabs">
          {units.map((u) => (
            <div
              key={u.unit_id}
              className={`puws-unit-tab${
                String(u.unit_id) === String(selectedUnit) ? " active" : ""
              }`}
              onClick={() => setSelectedUnit(String(u.unit_id))}
            >
              <div className="puws-unit-tab-name">
                {u.unit_name || `Unit ${u.unit_id}`}
              </div>
              <div className="puws-unit-tab-count">
                {u.total_products || 0} products
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Stats ────────────────────────────────── */}
      <div className="puws-stats tech-kpis tech-kpis--6">
  {[
    { cls: "s1", lbl: "Total Products", val: stats.totalProducts, icon: "fa-boxes-stacked", tone: "info" },
    { cls: "s2", lbl: "Allocated Qty", val: stats.totalAllocated, icon: "fa-right-left", tone: "purple" },
    {
      cls: "s3",
      lbl: "Returned Qty",
      val: stats.totalReturned,
      clickable: true,
      icon: "fa-rotate-left",
      tone: "success",
    },
    { cls: "s4", lbl: "Currently Out", val: stats.currentlyOut, icon: "fa-truck-fast", tone: "warning" },
    { cls: "s6", lbl: "Available Stock", val: stats.availableStock, icon: "fa-warehouse", tone: "success" },
    { cls: "s5", lbl: "Current Value", val: money(stats.currentValue), icon: "fa-sack-dollar", tone: "teal" },
  ].map((s) => (
    <div
      key={s.lbl}
      className={`puws-stat ${s.cls}${s.clickable ? " clickable" : ""} tech-kpi tech-tone-${s.tone}`}
      onClick={s.clickable ? openAllReturnedHistory : undefined}
      title={s.clickable ? "Click to view all returned items" : ""}
    >
      <div className={`puws-stat-icon tone-${s.tone} tech-kpi-icon`}>
        <i className={`fa-solid ${s.icon}`} aria-hidden="true"></i>
      </div>
      <div className="puws-stat-body tech-kpi-body">
        <div className="puws-stat-lbl tech-kpi-label">{s.lbl}</div>
        <div className="puws-stat-val tech-kpi-value">{s.val}</div>
      </div>
    </div>
  ))}
      </div>

      <AssetValueSummary
        unitId={selectedUnit && selectedUnit !== ALL_UNITS ? selectedUnit : undefined}
        refreshKey={products}
      />

      {monthFilter !== "ALL" && (
        <p className="puws-month-note" style={{ fontSize: 12, opacity: 0.75, margin: "-6px 0 12px" }}>
          <i className="fa-solid fa-circle-info" aria-hidden="true"></i>{" "}
          "Currently Out", "Available Stock" and "Current Value" reflect today's live
          stock status for products purchased in {monthKeyLabel(monthFilter)} — not a
          historical snapshot of stock as it stood that month.
        </p>
      )}

      {/* ── Main card ────────────────────────────── */}
      <div className="puws-card tech-card">
        <div className="puws-toolbar tech-toolbar">
          <div className="puws-toolbar-left tech-toolbar-title">
            <h5>
              Product Inventory —{" "}
              {selectedUnit === ALL_UNITS ? "All Units" : selectedUnitInfo?.unit_name || "—"}
            </h5>
            <p>
              {selectedUnit === ALL_UNITS
                ? "Search and filter inventory across every accessible unit."
                : "Search and filter allocated products of the selected unit."}
            </p>
          </div>

          <div className="puws-toolbar-right tech-filters">
            <div className="puws-search-wrap tech-search">
              <span className="puws-search-ico tech-search-ico">
                <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
              </span>
              <input
                className="puws-search"
                placeholder="Search SKU, name, model, serial, allotted user…"
                aria-label="Search products"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>

            <select
              className="puws-select tech-select"
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              title="Filter by purchase month"
              aria-label="Filter by month"
            >
              <option value="ALL">All Months</option>
              {monthOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <select
              className="puws-select tech-select"
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
              aria-label="Filter by brand"
            >
              <option value="ALL">All Brands</option>
              {brandOptions.map((brand) => (
                <option key={brand} value={brand}>
                  {brand}
                </option>
              ))}
            </select>

            <select
              className="puws-select tech-select tech-select--wide"
              value={holderFilter}
              onChange={(e) => setHolderFilter(e.target.value)}
              aria-label="Filter by allotted user"
            >
              <option value="ALL">All Allotted Users</option>
              {holderOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>

            <select
              className="puws-select tech-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by status"
            >
              <option value="ALL">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="RETURNED">Returned</option>
              <option value="DISCARDED">Discarded</option>
            </select>

            <select
              className="puws-select tech-select tech-select--rows"
              value={perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
              aria-label="Rows per page"
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n} rows
                </option>
              ))}
            </select>

            {(query || statusFilter !== "ACTIVE" || brandFilter !== "ALL" || holderFilter !== "ALL" || monthFilter !== "ALL") && (
              <button
                className="puws-btn puws-btn-ghost tech-reset-btn"
                aria-label="Reset filters"
                title="Reset filters"
                onClick={() => {
                  setQuery("");
                  setStatusFilter("ACTIVE");
                  setBrandFilter("ALL");
                  setHolderFilter("ALL");
                  setMonthFilter("ALL");
                  setPage(1);
                }}
              >
                <i className="fa-solid fa-filter-circle-xmark" aria-hidden="true"></i>
                <span className="tech-reset-label">Reset</span>
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="puws-table-wrap tech-table-wrap">
          {loadingProducts ? (
            <div className="puws-loader">
              <Spin lg />
              <p>Loading products…</p>
            </div>
          ) : (
            <table className="puws-table tech-table tech-table--products">
              <thead>
                <tr>
                  <th data-col="no" className="tech-th-center">S.No</th>
                  <th data-col="product">Product</th>
                  <th data-col="unit">Unit</th>
                  <th data-col="category">Category</th>
                  <th data-col="brand">Brand</th>
                  <th data-col="model">Model / Serial</th>
                  <th data-col="spec">Spec</th>
                  <th data-col="purchase">Purchase Date</th>
                  <th data-col="warranty-exp">Warranty Expiry</th>
                  <th data-col="warranty">Warranty Status</th>
                  <th data-col="end-life">End Life</th>
                  <th data-col="price">Price</th>
                  <th data-col="alloc" title="Allocated">Alloc.</th>
                  <th data-col="ret" title="Returned">Ret.</th>
                  <th data-col="out" title="Currently out">Out</th>
                  <th data-col="stock" title="Available stock">Stock</th>
                  <th data-col="status">Status</th>
                  <th data-col="actions">Actions</th>
                </tr>
              </thead>

              <tbody>
                {pageData.length === 0 ? (
                  <tr>
                    <td colSpan={18}>
                      <div className="puws-empty">
                        <div className="puws-empty-icon"><i className="fa-solid fa-box-open" aria-hidden="true"></i></div>
                        <h5>No products found</h5>
                        <p>
                          {monthFilter !== "ALL"
                            ? `No records found for ${monthKeyLabel(monthFilter)}.`
                            : selectedUnit === ALL_UNITS
                            ? "No products found across any accessible unit."
                            : "Select another unit or add products to this unit."}
                        </p>
                        {canCreate && hasConcreteUnitSelected && (
                          <button
                            className="puws-btn puws-btn-primary"
                            style={{ marginTop: 8 }}
                            onClick={openAdd}
                          >
                            <i className="fa-solid fa-plus" aria-hidden="true"></i> Add Product
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  pageData.map((p, idx) => (
                    <tr key={p.group_key || p.id}>
                      <td data-col="no">
                        <span className="puws-sno tech-sno">
                          {(page - 1) * perPage + idx + 1}
                        </span>
                      </td>

                      <td data-col="product">
                        <div className="puws-prod-cell tech-primary-cell">
                          <div className="puws-prod-av tech-avatar">
                            {p.name?.charAt(0)?.toUpperCase() || "P"}
                          </div>

                          <div className="tech-primary-text">
                            <div className="puws-prod-name tech-primary" title={p.name}>
                              {p.name}
                              {p.qty > 1 && (
                                <span className="puws-qty-pill">
                                  Qty: {p.qty}
                                </span>
                              )}
                            </div>

                            <div className="puws-prod-sku tech-secondary">
                              SKU: {p.sku || "—"}
                            </div>

                            {p.matched_item && (
                              // Task 4: searching a non-representative group
                              // member's SKU/serial must return the group
                              // with that specific matching item clearly
                              // identifiable, not just the group's generic
                              // representative SKU/serial shown above.
                              <div className="puws-sku-note" style={{ color: "#B45309", fontWeight: 700 }}>
                                Matched: {p.matched_item.sku || "—"}
                                {p.matched_item.serial_no ? ` • Serial ${p.matched_item.serial_no}` : ""}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      <td data-col="unit">
                        <UnitChip name={p.unit_name || "—"} />
                      </td>

                      <td data-col="category">
                        <span className="chip chip-neutral" title={p.category || "—"}>
                          <span className="chip-label">{p.category || "—"}</span>
                        </span>
                      </td>

                      <td data-col="brand" title={p.brand || "—"}>{p.brand || "—"}</td>

                      <td data-col="model">
                        <div className="tech-model">{p.model_no || "—"}</div>
                        <div className="tech-serial">{p.serial_no || "—"}</div>
                      </td>

                      <td data-col="spec">
                        <div className="puws-spec">{specLine(p)}</div>

                        {(p.has_monitor ||
                          p.has_mouse ||
                          p.has_keyboard ||
                          p.has_adapter ||
                          p.has_cpu) && (
                          <div className="puws-chips">
                            {p.has_cpu && <span className="puws-chip">CPU</span>}
                            {p.has_monitor && (
                              <span className="puws-chip">Monitor</span>
                            )}
                            {p.has_mouse && (
                              <span className="puws-chip">Mouse</span>
                            )}
                            {p.has_keyboard && (
                              <span className="puws-chip">Keyboard</span>
                            )}
                            {p.has_adapter && (
                              <span className="puws-chip">Adapter</span>
                            )}
                          </div>
                        )}
                      </td>

                      <td data-col="purchase">
                        <span className="puws-date">
                          {fmtDateOnly(p.purchase_date)}
                        </span>
                      </td>

                      <td data-col="warranty-exp">
                        <span className="puws-date">
                          {fmtDateOnly(p.warranty_expiry_date)}
                        </span>
                      </td>

                      <td data-col="warranty">
                        <WarrantyBadge status={p.warranty_status} />
                      </td>

                      <td data-col="end-life">
                        {(() => {
                          // Derived per item (purchase date + asset life,
                          // or a Life Review override) -- a group only
                          // shows one value when every item agrees.
                          const end = groupLifeField(p, "expected_end_date");
                          const life = groupLifeField(p, "useful_life_label");
                          if (end === "VARIES" || life === "VARIES") {
                            return <span className="puws-date" title="Items in this group have different asset life">Varies</span>;
                          }
                          if (!end) return <span className="puws-date">{life || "—"}</span>;
                          const lifecycle = groupLifeField(p, "lifecycle");
                          return (
                            <span className="puws-date" title={life ? `Asset Life: ${life}` : undefined}>
                              {fmtDateOnly(end)}
                              {life && <small className="d-block">{life}</small>}
                              {lifecycle && lifecycle !== "VARIES" && <LifecycleBadge lifecycle={lifecycle} />}
                            </span>
                          );
                        })()}
                      </td>

                      <td data-col="price">
                        <span className="puws-money">{money(p.price)}</span>
                      </td>

                      <td data-col="alloc">
                        <span className="puws-num">
                          {p.total_allocated ?? 0}
                        </span>
                      </td>

                      <td data-col="ret">
                      <button
                        type="button"
                        className="puws-ret-link"
                        disabled={Number(p.total_returned || 0) <= 0}
                        title={
                          Number(p.total_returned || 0) > 0
                            ? "View returned product details"
                            : "No returned item"
                        }
                        onClick={() => openAllocationHistory(p, "RETURNED")}
                      >
                        {p.total_returned ?? 0}
                      </button>
                    </td>

                      <td data-col="out">
                        <span className="puws-num">{p.currently_out ?? 0}</span>
                      </td>

                      <td data-col="stock">
                        <span
                          className={`puws-stock ${
                            Number(p.available_stock || 0) > 0 ? "ok" : "zero"
                          }`}
                        >
                          {p.available_stock ?? 0}
                        </span>
                      </td>

                      <td data-col="status">
                        <StatusBadge status={p.status || "ACTIVE"} isDiscarded={p.is_discarded} />
                      </td>

                      <td data-col="actions">
                        <div className="puws-actions tech-actions">
                          <button
                            className="puws-action-btn view tech-act tech-act--view"
                            title="View serials"
                            aria-label="View serials"
                            onClick={() => openGroupView(p)}
                          >
                            <i className="fa-solid fa-eye" aria-hidden="true"></i>
                          </button>
                          <button
                            className="puws-action-btn history tech-act tech-act--history"
                            title="Allocation history"
                            aria-label="Allocation history"
                           onClick={() => openAllocationHistory(p, "ALL")}
                          >
                            <i className="fa-solid fa-clock-rotate-left" aria-hidden="true"></i>
                          </button>

                          <button
                            className="puws-action-btn invoice tech-act tech-act--print"
                            title="Product invoices"
                            aria-label="Product invoices"
                            onClick={() => openInvoices(p)}
                          >
                            <i className="fa-solid fa-file-invoice" aria-hidden="true"></i>
                          </button>

                          {(canEdit || (canGroupEdit && (p.items?.length || 1) > 1)) && (
                            <button
                              className="puws-action-btn edit tech-act tech-act--edit"
                              // Group-vs-single editing is a question of how
                              // many actual DB rows/SKUs this group card
                              // represents (p.items.length), NOT the summed
                              // "Qty" value -- a single bulk row with its own
                              // qty=5 is still exactly ONE row to edit, and
                              // must open the single-row editor (which now
                              // lets that qty be corrected) rather than the
                              // group editor (which assumes N separate
                              // serialized rows and never exposes qty at all).
                              //
                              // Discarded products are historical records --
                              // normal Edit must not reach them (backend
                              // also rejects the update directly). p.is_discarded
                              // is only true once EVERY item in this group
                              // has been discarded, so this never blocks a
                              // group edit while any item is still active.
                              title={
                                p.is_discarded
                                  ? "This product has been discarded and is retained as a historical record."
                                  : (p.items?.length || 1) > 1
                                  ? "Edit asset group"
                                  : "Edit"
                              }
                              aria-label={
                                p.is_discarded
                                  ? "Discarded product cannot be edited"
                                  : (p.items?.length || 1) > 1
                                  ? "Edit asset group"
                                  : "Edit"
                              }
                              disabled={p.is_discarded}
                              onClick={() =>
                                !p.is_discarded &&
                                ((p.items?.length || 1) > 1
                                  ? openEditGroup(p)
                                  : openEditSingle(p.items?.[0] || p))
                              }
                            >
                              <i className="fa-solid fa-pen" aria-hidden="true"></i>
                            </button>
                          )}

                          {canDiscard && !p.is_discarded && (
                            // Discard is always against exactly ONE
                            // individual product id (PUT
                            // /products/:id/discard) -- there is no
                            // "discard group" backend operation. A group
                            // that IS exactly one physical row can discard
                            // it directly; a group representing more than
                            // one physical item opens a picker so the user
                            // explicitly chooses WHICH serial, rather than
                            // this button silently discarding "the whole
                            // group" (which is never actually possible,
                            // but was previously just hidden here instead
                            // of offering a way to reach it at all).
                            <button
                              className="puws-action-btn discard tech-act tech-act--cancel"
                              title="Discard Product"
                              aria-label={`Discard ${p.name || "product"}`}
                              onClick={() =>
                                (p.items?.length || 1) === 1
                                  ? openDiscardModal(p.items?.[0] || p)
                                  : openDiscardPicker(p)
                              }
                            >
                              <i className="fa-solid fa-box-archive" aria-hidden="true"></i>
                            </button>
                          )}

                          {canDelete && (
                            <button
                              className="puws-action-btn delete tech-act tech-act--delete"
                              title={p.is_discarded ? "This product has been discarded and is retained as a historical record." : "Delete / inactive"}
                              aria-label={p.is_discarded ? "Discarded product cannot be deleted" : "Delete / inactive"}
                              disabled={deleting || p.is_discarded}
                              onClick={() =>
                                inactiveProducts(
                                  p.items || [p],
                                  (p.items?.length || 1) > 1 ? "products" : "product"
                                )
                              }
                            >
                              <i className="fa-solid fa-trash" aria-hidden="true"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="puws-footer tech-footer">
          <span>
            Showing{" "}
            <strong>
              {filtered.length === 0 ? 0 : (page - 1) * perPage + 1}
            </strong>
            –<strong>{Math.min(page * perPage, filtered.length)}</strong> of{" "}
            <strong>{filtered.length}</strong>
          </span>

          {totalPages > 1 && (
            <div className="puws-page-btns tech-pagination">
              <button
                className="puws-page-btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                ‹
              </button>

              {compactPageItems([...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  className={`puws-page-btn${i + 1 === page ? " active" : ""}`}
                  onClick={() => setPage(i + 1)}
                >
                  {i + 1}
                </button>
              )), page, {
                renderEllipsis: (key) => (
                  <span key={key} className="puws-page-btn puws-page-gap" aria-hidden="true">…</span>
                ),
              })}

              <button
                className="puws-page-btn"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                ›
              </button>
            </div>
          )}
        </div>
      </div>
      {showAllocHistory && (
        <div
          className="puws-overlay"
          onClick={(e) =>
            e.target === e.currentTarget && setShowAllocHistory(false)
          }
        >
          <div className="puws-modal puws-modal-wide puws-history-modal">
            <div className="puws-modal-hdr">
              <div>
                <h2 className="puws-modal-title">
                  {allocHistoryMode === "RETURNED"
                    ? "Returned Product Details"
                    : "Allocation History"}
                </h2>
                <p className="puws-modal-subtitle">
                  {allocHistoryProduct?.name || "Product"}{" "}
                  {allocHistoryProduct?.serial_no
                    ? `• ${allocHistoryProduct.serial_no}`
                    : ""}
                </p>
                <p className="text-muted small mb-0">
                  Created: {fmtDate(allocHistoryProduct?.created_at)} &nbsp;|&nbsp; Last
                  Updated: {fmtDate(allocHistoryProduct?.updated_at)}
                </p>
              </div>

              <button
                className="puws-x"
                onClick={() => setShowAllocHistory(false)}
                aria-label="Close"
                title="Close"
              >
                <i className="fa-solid fa-xmark" aria-hidden="true"></i>
              </button>
            </div>

            <div className="puws-modal-body puws-history-body">
              <div className="puws-history-summary puws-history-summary-5">
                {/* <div>
                  <span>Issued Qty</span>
                  <strong>{allocHistorySummary?.total_allocated || 0}</strong>
                  <small>Total quantity ever issued (lifetime)</small>
                </div> */}

                <div>
                  <span>Returned Qty</span>
                  <strong>{allocHistorySummary?.total_returned || 0}</strong>
                  <small>Total quantity ever returned (lifetime)</small>
                </div>

                <div>
                  <span>In Stock</span>
                  <strong>{historyAvailableStock}</strong>
                  <small>Currently available</small>
                </div>

                <div>
                  <span>Currently Out</span>
                  <strong>{allocHistorySummary?.currently_out || 0}</strong>
                  <small>Active allocations now</small>
                </div>

                <div>
                  <span>Currently With</span>
                  <strong>
                    {(allocHistorySummary?.current_holders || []).length}{" "}
                    {(allocHistorySummary?.current_holders || []).length === 1 ? "User" : "Users"}
                  </strong>
                  <small
                    className="puws-history-holders-preview"
                    title={(allocHistorySummary?.current_holders || []).join(", ") || "No active holders"}
                  >
                    {(allocHistorySummary?.current_holders || []).length
                      ? `${allocHistorySummary.current_holders.slice(0, 2).join(", ")}${
                          allocHistorySummary.current_holders.length > 2
                            ? ` +${allocHistorySummary.current_holders.length - 2} more`
                            : ""
                        }`
                      : "-"}
                  </small>
                </div>
              </div>

              <div className="puws-history-rule-box">
                <span><i className="fa-solid fa-circle-info" aria-hidden="true"></i></span>
                <p>
                  <strong>Logic:</strong> An item shows <b>In Stock</b> only if it is not
                  currently allocated to anyone right now — a past return does not count if
                  the item was later re-allocated. Otherwise it shows{" "}
                  <b>Currently Allocated</b> with its current holder.
                </p>
              </div>

              {loadingAllocHistory ? (
                <div className="puws-loader">
                  <Spin lg />
                  <p>Loading allocation history…</p>
                </div>
              ) : (
                <>
                  {/* OPTION 2: Card wise issue / return history */}
                  <div className="puws-history-section-head">
                    <div>
                      <h4>Issue / Return Card History</h4>
                      <p>
                        Kis ko issue hua tha, ab kiske paas hai, aur stock status.
                        {allocHistoryMode === "RETURNED" && (
                          <> Showing returned records only -- active (currently out)
                          allocations are counted above in "Currently Out" but not
                          listed here.</>
                        )}
                      </p>
                    </div>
                    <span className="puws-return-pill">
                      {allocHistoryMode === "RETURNED" ? "Returned Records" : "Records"}:{" "}
                      {visibleAllocHistoryRows.length}
                    </span>
                  </div>

                  {visibleAllocHistoryRows.length === 0 ? (
                    <div className="puws-return-empty">
                      No allocation history found for this product.
                    </div>
                  ) : (
                    <div className="puws-history-cards-grid">
                      {visibleAllocHistoryRows.map((row, index) => {
                        const isReturned =
                          String(row.status || "").toUpperCase() === "RETURNED";
                        const isAllocated =
                          String(row.status || "").toUpperCase() === "ALLOCATED";
                        // Current state of the physical item this record belongs to --
                        // may differ from this record's OWN status if the item was
                        // returned here but re-allocated in a later record.
                        const currentHolder = currentHolderByItemId.get(row.source_product_id);
                        const isCurrentlyAllocated = Boolean(currentHolder);

                        return (
                          <div
                            className={`puws-movement-card ${
                              isCurrentlyAllocated ? "assigned" : isReturned ? "returned" : isAllocated ? "assigned" : "other"
                            }`}
                            key={`card-${row.source_product_id}-${row.id}-${index}`}
                          >
                            <div className="puws-movement-card-top">
                              <div>
                                <span className="puws-movement-count">#{index + 1}</span>
                                <h5>{row.product_name || allocHistoryProduct?.name || "Product"}</h5>
                                <p>
                                  <b>{row.sku || "-"}</b>
                                  <span>{row.serial_no || "-"}</span>
                                </p>
                              </div>

                              <span
                                className={`puws-flow-status ${
                                  isCurrentlyAllocated ? "assigned" : isReturned ? "stock" : "inactive"
                                }`}
                              >
                                {isCurrentlyAllocated
                                  ? isAllocated
                                    ? "Assigned"
                                    : "Currently Allocated"
                                  : isReturned
                                  ? "In Stock"
                                  : row.status || "-"}
                              </span>
                            </div>

                            <div className="puws-flow-line">
                              <div className="puws-flow-step done">
                                <span>1</span>
                                <div>
                                  <small>Issued To</small>
                                  <strong>{row.user_name || "-"}</strong>
                                  <em>{fmtDateTime(row.allocated_at)}</em>
                                </div>
                              </div>

                              <div className={`puws-flow-step ${isReturned ? "done" : "active"}`}>
                                <span>2</span>
                                <div>
                                  <small>
                                    {isReturned ? "Returned To Stock" : "Currently With"}
                                  </small>
                                  <strong>
                                    {isReturned ? "Stock / Store" : row.user_name || "-"}
                                  </strong>
                                  <em>
                                    {isReturned
                                      ? fmtDateTime(row.return_at)
                                      : "Not returned yet"}
                                  </em>
                                </div>
                              </div>
                            </div>

                            <div className="puws-card-meta-grid">
                              <div>
                                <small>Unit</small>
                                <strong>{row.unit_name || "-"}</strong>
                              </div>
                              <div>
                                <small>Qty</small>
                                <strong>{row.qty || 0}</strong>
                              </div>
                              <div>
                                <small>Stock / Assignment</small>
                                <strong>
                                  {isCurrentlyAllocated
                                    ? `With ${currentHolder}`
                                    : isReturned
                                    ? "In Stock"
                                    : `With ${row.user_name || "Assigned User"}`}
                                </strong>
                              </div>
                            </div>

                            {row.notes && row.notes !== "-" && (
                              <div className="puws-card-note">Note: {row.notes}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* OPTION 3: Table format detailed history */}
                  <div className="puws-history-section-head table-head">
                    <div>
                      <h4>Detailed Table History</h4>
                      <p>
                        Issued user, current holder, returned date aur stock/assignment ek hi table me.
                      </p>
                    </div>
                  </div>

                  <div className="puws-table-wrap puws-history-table-wrap">
                    <table className="puws-table puws-history-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Product</th>
                          <th>Issued To</th>
                          <th>Current Holder</th>
                          <th>Unit</th>
                          <th>SKU / Serial</th>
                          <th>Qty</th>
                          <th>Issue Date</th>
                          <th>Return Date</th>
                          <th>Status</th>
                          <th>Stock / Assignment</th>
                          <th>Notes</th>
                        </tr>
                      </thead>

                      <tbody>
                        {visibleAllocHistoryRows.length === 0 ? (
                          <tr>
                            <td colSpan={12}>
                              <div className="puws-empty">
                                <div className="puws-empty-icon"><i className="fa-solid fa-clock-rotate-left" aria-hidden="true"></i></div>
                                <h5>No allocation history found</h5>
                                <p>This product has not been allocated yet.</p>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          visibleAllocHistoryRows.map((row, index) => {
                            const isReturned =
                              String(row.status || "").toUpperCase() === "RETURNED";
                            const isAllocated =
                              String(row.status || "").toUpperCase() === "ALLOCATED";
                            const currentHolder = currentHolderByItemId.get(row.source_product_id);
                            const isCurrentlyAllocated = Boolean(currentHolder);

                            return (
                              <tr key={`table-${row.source_product_id}-${row.id}-${index}`}>
                                <td>{index + 1}</td>

                                <td>
                                  <strong>
                                    {row.product_name || allocHistoryProduct?.name || "Product"}
                                  </strong>
                                </td>

                                <td>
                                  <strong>{row.user_name || "-"}</strong>
                                  <div className="puws-history-muted">Original recipient</div>
                                </td>

                                <td>
                                  {isCurrentlyAllocated ? (
                                    <>
                                      <strong>{currentHolder}</strong>
                                      <div className="puws-current-holder">
                                        {isAllocated ? "Currently with this user" : "Re-allocated to this user"}
                                      </div>
                                    </>
                                  ) : (
                                    <span className="puws-stock ok">In Stock</span>
                                  )}
                                </td>

                                <td>{row.unit_name || "-"}</td>

                                <td>
                                  <div>
                                    <strong>{row.sku || "-"}</strong>
                                  </div>
                                  <small>{row.serial_no || "-"}</small>
                                </td>

                                <td>{row.qty || 0}</td>

                                <td>{fmtDateTime(row.allocated_at)}</td>

                                <td>{isReturned ? fmtDateTime(row.return_at) : "-"}</td>

                                <td>
                                  <span
                                    className={`puws-badge ${
                                      isAllocated
                                        ? "active"
                                        : isReturned
                                        ? "warranty"
                                        : "inactive"
                                    }`}
                                  >
                                    {isReturned ? "RETURNED" : isAllocated ? "ASSIGNED" : row.status || "-"}
                                  </span>
                                </td>

                                <td>
                                  <span
                                    className={`puws-assignment-pill ${
                                      isCurrentlyAllocated ? "assigned" : "stock"
                                    }`}
                                  >
                                    {isCurrentlyAllocated
                                      ? `With ${currentHolder}`
                                      : "In Stock"}
                                  </span>
                                </td>

                                <td>{row.notes || "-"}</td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>

            <div className="puws-modal-ftr">
              <button
                className="puws-btn puws-btn-ghost"
                onClick={() => setShowAllocHistory(false)}
              >
                <i className="fa-solid fa-xmark" aria-hidden="true"></i> Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ ADD PRODUCT MODAL ══ */}
      {showAdd && (
        <div
          className="puws-overlay"
          onClick={(e) => e.target === e.currentTarget && setShowAdd(false)}
        >
          <div className="puws-modal">
            <div className="puws-modal-hdr">
              <h2 className="puws-modal-title">
                Add Product to {selectedUnitInfo?.unit_name || "Selected Unit"}
              </h2>

              <button
                className="puws-modal-close"
                onClick={() => setShowAdd(false)}
                aria-label="Close"
                title="Close"
              >
                <i className="fa-solid fa-xmark" aria-hidden="true"></i>
              </button>
            </div>

            <div className="puws-modal-body">
              <div className="puws-unit-info">
                <div>
                  <strong>Unit:</strong> {selectedUnitInfo?.unit_name || "—"}
                </div>
                <div className="puws-sku-note">
                  SKU auto-generated after save
                </div>
              </div>

              {renderProductForm(form, setField, false)}
            </div>

            <div className="puws-modal-ftr">
              <button
                className="puws-btn puws-btn-ghost"
                onClick={() => setShowAdd(false)}
                disabled={saving}
              >
                <i className="fa-solid fa-xmark" aria-hidden="true"></i> Cancel
              </button>

              <button
                className="puws-btn puws-btn-primary"
                onClick={handleAdd}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Spin white /> Saving…
                  </>
                ) : serialCount > 1 ? (
                  <>
                    <i className="fa-solid fa-floppy-disk" aria-hidden="true"></i> Save {serialCount} Products
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-floppy-disk" aria-hidden="true"></i> Save Product
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ EDIT MODAL ══ */}
      {showEdit && (
        <div
          className="puws-overlay"
          onClick={(e) => e.target === e.currentTarget && setShowEdit(false)}
        >
          <div className="puws-modal">
            <div className="puws-modal-hdr">
              <h2 className="puws-modal-title">
                {editMode === "group" ? "Edit Asset Group" : "Edit Product"}
              </h2>

              <button
                className="puws-modal-close"
                onClick={() => setShowEdit(false)}
                aria-label="Close"
                title="Close"
              >
                <i className="fa-solid fa-xmark" aria-hidden="true"></i>
              </button>
            </div>

            <div className="puws-modal-body">
              {renderProductForm(editForm, setEditField, true)}
            </div>

            <div className="puws-modal-ftr">
              <button
                className="puws-btn puws-btn-ghost"
                onClick={() => setShowEdit(false)}
                disabled={saving}
              >
                <i className="fa-solid fa-xmark" aria-hidden="true"></i> Cancel
              </button>

              <button
                className="puws-btn puws-btn-primary"
                onClick={handleEditSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Spin white /> Updating…
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-floppy-disk" aria-hidden="true"></i> Update Product
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ VIEW SERIALS MODAL ══ */}
      {showView && viewGroup && (
        <div
          className="puws-overlay"
          onClick={(e) => e.target === e.currentTarget && setShowView(false)}
        >
          <div className="puws-modal puws-view-modal">
            <div className="puws-modal-hdr">
              <h2 className="puws-modal-title">
                {viewGroup.name} — Serial Items
              </h2>

              <button
                className="puws-modal-close"
                onClick={() => setShowView(false)}
                aria-label="Close"
                title="Close"
              >
                <i className="fa-solid fa-xmark" aria-hidden="true"></i>
              </button>
            </div>

            <div className="puws-modal-body">
              <div className="puws-unit-info">
                <div>
                  <div>
                    <strong>Total Items:</strong> {viewGroup.items?.length || 0}
                  </div>
                  <div className="puws-sku-note">
                    You can edit or inactive individual serial items.
                  </div>
                </div>
                {canAddItem && (
                  <button
                    type="button"
                    className="puws-btn puws-btn-primary"
                    onClick={() => openAddItem(viewGroup)}
                  >
                    <i className="fa-solid fa-plus" aria-hidden="true"></i> Add Asset Item
                  </button>
                )}
              </div>

              <div className="puws-mini-table-wrap">
                <table className="puws-mini-table">
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Serial No.</th>
                      <th>Model</th>
                      <th>Asset Life</th>
                      <th>End Life Date</th>
                      <th>Life Status</th>
                      <th>Status</th>
                      <th style={{ textAlign: "right" }}>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {(viewGroup.items || []).map((item) => (
                      <tr key={item.id}>
                        <td>{item.sku || "—"}</td>
                        <td>{item.serial_no || "—"}</td>
                        <td>{item.model_no || "—"}</td>
                        <td>{item.useful_life_label || "—"}</td>
                        <td>
                          {item.expected_end_date ? fmtDateOnly(item.expected_end_date) : "—"}
                          {item.remaining_life_label && (
                            <small className="d-block">{item.remaining_life_label}</small>
                          )}
                        </td>
                        <td>
                          <LifecycleBadge lifecycle={item.lifecycle} />
                        </td>
                        <td>
                          <StatusBadge status={item.status || "ACTIVE"} isDiscarded={item.is_discarded} />
                        </td>
                        <td>
                          <div className="puws-actions right">
                            <button
                              className="puws-action-btn invoice tech-act tech-act--print"
                              title="Invoices"
                              aria-label="Invoices"
                              onClick={() => {
                                setShowView(false);
                                openInvoices(item);
                              }}
                            >
                              <i className="fa-solid fa-file-invoice" aria-hidden="true"></i>
                            </button>

                            {canEdit && (
                              <button
                                className="puws-action-btn edit tech-act tech-act--edit"
                                title={
                                  item.is_discarded
                                    ? "This product has been discarded and is retained as a historical record."
                                    : "Edit"
                                }
                                aria-label={
                                  item.is_discarded
                                    ? "Discarded product cannot be edited"
                                    : "Edit product"
                                }
                                disabled={item.is_discarded}
                                onClick={() => {
                                  if (item.is_discarded) return;
                                  setShowView(false);
                                  openEditSingle(item);
                                }}
                              >
                                <i className="fa-solid fa-pen" aria-hidden="true"></i>
                              </button>
                            )}

                            {canDiscard && !item.is_discarded && (
                              <button
                                className="puws-action-btn discard tech-act tech-act--cancel"
                                title="Discard Product"
                                aria-label={`Discard ${item.name || "product"}`}
                                onClick={() => {
                                  setShowView(false);
                                  openDiscardModal(item);
                                }}
                              >
                                <i className="fa-solid fa-box-archive" aria-hidden="true"></i>
                              </button>
                            )}

                            {canDelete && (
                              <button
                                className="puws-action-btn delete tech-act tech-act--delete"
                                title={
                                  item.is_discarded
                                    ? "This product has been discarded and is retained as a historical record."
                                    : "Delete / inactive"
                                }
                                aria-label={
                                  item.is_discarded
                                    ? "Discarded product cannot be deleted"
                                    : "Delete product"
                                }
                                disabled={deleting || item.is_discarded}
                                onClick={() =>
                                  inactiveProducts([item], "product")
                                }
                              >
                                <i className="fa-solid fa-trash" aria-hidden="true"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="puws-modal-ftr">
              <button
                className="puws-btn puws-btn-ghost"
                onClick={() => setShowView(false)}
              >
                <i className="fa-solid fa-xmark" aria-hidden="true"></i> Close
              </button>

              {(canEdit || (canGroupEdit && (viewGroup?.items?.length || 1) > 1)) && (
                <button
                  className="puws-btn puws-btn-primary"
                  onClick={() => {
                    setShowView(false);
                    openEditGroup(viewGroup);
                  }}
                >
                  <i className="fa-solid fa-pen" aria-hidden="true"></i> Edit Group
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══ ADD ASSET ITEM (to the open Serial Items group) ══
          Rendered after the Serial Items modal so it stacks on top of it;
          the Serial Items modal stays open underneath. */}
      {addItemSource && addItemForm && (
        <div
          className="puws-overlay"
          onClick={(e) => e.target === e.currentTarget && closeAddItem()}
        >
          <div className="puws-modal" role="dialog" aria-modal="true" aria-labelledby="puws-add-item-title">
            <div className="puws-modal-hdr">
              <h2 className="puws-modal-title" id="puws-add-item-title">
                Add Asset Item — {addItemSource.name}
              </h2>
              <button
                className="puws-modal-close"
                onClick={closeAddItem}
                aria-label="Close"
                title="Close"
              >
                <i className="fa-solid fa-xmark" aria-hidden="true"></i>
              </button>
            </div>

            <div className="puws-modal-body">
              <div className="puws-unit-info">
                <div>
                  <strong>Asset group details</strong>
                  <div className="puws-sku-note">
                    Taken from this group so the new item stays in it. Use Edit Group to change them.
                  </div>
                </div>
              </div>

              <div className="puws-form-grid">
                {[
                  ["Asset Name", addItemSource.name, "span2"],
                  ["Unit", addItemSource.unit_name || "—"],
                  ["Category", addItemSource.category],
                  ["Brand", addItemSource.brand],
                  ["Model No.", addItemSource.model_no],
                  ["Price (₹)", addItemSource.price != null ? Number(addItemSource.price).toFixed(2) : ""],
                  ["Purchase Date", addItemSource.purchase_date ? fmtDateOnly(addItemSource.purchase_date) : ""],
                  ["Warranty Expiry", addItemSource.warranty_expiry_date ? fmtDateOnly(addItemSource.warranty_expiry_date) : ""],
                  ["Status", addItemSource.status === "INACTIVE" ? "Inactive" : "Active"],
                  ...(addItemSource.processor || addItemSource.ram_gb || addItemSource.ssd_gb || addItemSource.hdd_gb
                    ? [
                        ["Processor", addItemSource.processor],
                        ["RAM (GB)", addItemSource.ram_gb],
                        ["SSD / HDD (GB)", [addItemSource.ssd_gb, addItemSource.hdd_gb].filter((v) => v != null).join(" / ")],
                      ]
                    : []),
                ].map(([label, value, span]) => (
                  <div key={label} className={span || undefined}>
                    <label className="puws-label">{label}</label>
                    <input className="puws-input" value={value ?? ""} placeholder="—" disabled readOnly />
                  </div>
                ))}

                <div className="span3">
                  <label className="puws-label" htmlFor="puws-add-item-serial">
                    Serial Number<span className="req">*</span>
                  </label>
                  <input
                    id="puws-add-item-serial"
                    className="puws-input"
                    placeholder="Serial number of the new item"
                    value={addItemForm.serial_no}
                    autoFocus
                    onChange={(e) => setAddItemField("serial_no", e.target.value)}
                  />
                  <div className="puws-sku-note">SKU is generated automatically when the item is saved.</div>
                </div>

                <div>
                  <label className="puws-label" htmlFor="puws-add-item-warranty">Warranty Start</label>
                  <input
                    id="puws-add-item-warranty"
                    type="date"
                    className="puws-input"
                    value={addItemForm.warranty_date}
                    onChange={(e) => setAddItemField("warranty_date", e.target.value)}
                  />
                </div>

                <div>
                  <label className="puws-label" htmlFor="puws-add-item-life">Asset Life</label>
                  <input
                    id="puws-add-item-life"
                    type="number"
                    min="1"
                    step="1"
                    className="puws-input"
                    placeholder="e.g. 5 (optional)"
                    value={addItemForm.useful_life_value ?? ""}
                    onChange={(e) => setAddItemField("useful_life_value", e.target.value)}
                  />
                </div>

                <div>
                  <label className="puws-label" htmlFor="puws-add-item-life-unit">Asset Life Unit</label>
                  <select
                    id="puws-add-item-life-unit"
                    className="puws-input"
                    value={addItemForm.useful_life_unit || "YEARS"}
                    onChange={(e) => setAddItemField("useful_life_unit", e.target.value)}
                  >
                    {LIFE_UNITS.map((u) => (
                      <option key={u.value} value={u.value}>{u.label}</option>
                    ))}
                  </select>
                </div>

                <div className="span3">
                  <label className="puws-label">Accessories</label>
                  <div className="puws-checks">
                    {ACCESSORIES.map(([k, lbl]) => (
                      <label key={k} className={`puws-check-item${addItemForm[k] ? " checked" : ""}`}>
                        <input
                          type="checkbox"
                          checked={!!addItemForm[k]}
                          onChange={(e) => setAddItemField(k, e.target.checked)}
                        />
                        {addItemForm[k] ? "✓ " : ""}
                        {lbl}
                      </label>
                    ))}
                  </div>
                </div>

                {addItemError && (
                  <div className="span3 puws-field-error" role="alert">{addItemError}</div>
                )}
              </div>
            </div>

            <div className="puws-modal-ftr">
              <button className="puws-btn puws-btn-ghost" onClick={closeAddItem} disabled={addingItem}>
                <i className="fa-solid fa-xmark" aria-hidden="true"></i> Cancel
              </button>
              <button className="puws-btn puws-btn-primary" onClick={handleAddItemSave} disabled={addingItem}>
                {addingItem ? (
                  <>
                    <Spin white /> Saving...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-plus" aria-hidden="true"></i> Add Asset Item
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ SELECT PRODUCT TO DISCARD (serial-wise picker for Qty > 1) ══
          Opened from the main row's Discard action when a group represents
          more than one physical item. Sourced from the SAME already-
          fetched group.items the View Serials modal above uses -- no new
          API call. Each row's own Discard button opens the EXISTING
          Discard modal below for that exact product id; this popup itself
          never discards anything. */}
      {showDiscardPicker && discardPickerGroup && (
        <div
          className="puws-overlay"
          onClick={(e) => e.target === e.currentTarget && setShowDiscardPicker(false)}
        >
          <div className="puws-modal puws-modal-wide puws-discard-picker-modal">
            <div className="puws-modal-hdr">
              <h2 className="puws-modal-title">
                {discardPickerGroup.name} — Select Product to Discard
              </h2>

              <button
                className="puws-modal-close"
                onClick={() => setShowDiscardPicker(false)}
                aria-label="Close"
                title="Close"
              >
                <i className="fa-solid fa-xmark" aria-hidden="true"></i>
              </button>
            </div>

            <div className="puws-modal-body">
              {(() => {
                const items = discardPickerGroup.items || [];
                const discardedCount = items.filter((i) => i.is_discarded).length;
                const usableCount = items.length - discardedCount;

                return (
                  <div className="puws-unit-info">
                    <div>
                      <strong>Group Qty:</strong> {items.length}
                    </div>
                    <div>
                      <strong>Active/Usable:</strong> {usableCount} •{" "}
                      <strong>Discarded:</strong> {discardedCount}
                    </div>
                    <div className="puws-sku-note">
                      Discard is always against one individual product/serial. Allocated or reserved items cannot be discarded until returned/released.
                    </div>
                  </div>
                );
              })()}

              <div className="puws-mini-table-wrap">
                <table className="puws-mini-table">
                  <thead>
                    <tr>
                      <th>Serial No.</th>
                      <th>SKU / Product ID</th>
                      <th>Model</th>
                      <th>Purchase Date</th>
                      <th>Operational Status</th>
                      <th>Allocation</th>
                      <th>Discard Status</th>
                      <th style={{ textAlign: "right" }}>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {(discardPickerGroup.items || []).map((item) => {
                      const isAllocated = Number(item.currently_out || 0) > 0;
                      // Task §11: New Joiner reservation must also block
                      // discard, same as an active allocation -- backend
                      // now enforces this too (PRODUCT_CURRENTLY_RESERVED,
                      // see productController.js#discard), this is only
                      // the frontend's preemptive read of the same
                      // reserved_qty field getUnitWiseProducts now returns.
                      const isReserved = !isAllocated && Number(item.reserved_qty || 0) > 0;
                      const blockedReason = isAllocated
                        ? "Currently allocated. Return this product before discarding."
                        : isReserved
                        ? "Currently reserved for a New Joiner request. Cancel or fulfil that reservation before discarding."
                        : "";

                      return (
                        <tr key={item.id}>
                          <td>{item.serial_no || "Not Available"}</td>
                          <td>
                            {item.sku || "—"}
                            <div className="puws-sku-note">ID: {item.id}</div>
                          </td>
                          <td>{item.model_no || "—"}</td>
                          <td>{fmtDateOnly(item.purchase_date)}</td>
                          <td>
                            <span className={`puws-badge ${item.status === "INACTIVE" ? "inactive" : "active"}`}>
                              {item.status === "INACTIVE" ? "Inactive" : "Active"}
                            </span>
                          </td>
                          <td>
                            {isAllocated ? (
                              <span className="puws-badge inactive">Allocated</span>
                            ) : isReserved ? (
                              <span className="puws-badge inactive">Reserved</span>
                            ) : (
                              <span className="puws-badge active">Available</span>
                            )}
                          </td>
                          <td>
                            {item.is_discarded ? (
                              <>
                                <span className="puws-badge discarded">Discarded</span>
                                <div className="puws-sku-note">
                                  {fmtDateOnly(item.discarded_at)}
                                  {item.discard_reason ? ` • ${item.discard_reason}` : ""}
                                </div>
                              </>
                            ) : (
                              <span className="puws-badge active">Active</span>
                            )}
                          </td>
                          <td>
                            <div className="puws-actions right">
                              {item.is_discarded ? (
                                <span className="puws-sku-note">Already discarded</span>
                              ) : blockedReason ? (
                                <button
                                  className="puws-action-btn discard tech-act tech-act--cancel"
                                  disabled
                                  title={blockedReason}
                                  aria-label={blockedReason}
                                >
                                  <i className="fa-solid fa-lock" aria-hidden="true"></i>
                                </button>
                              ) : canDiscard ? (
                                <button
                                  className="puws-action-btn discard tech-act tech-act--cancel"
                                  title="Discard Product"
                                  aria-label={`Discard ${item.name || "product"}`}
                                  onClick={() => openDiscardModal(item)}
                                >
                                  <i className="fa-solid fa-box-archive" aria-hidden="true"></i>
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="puws-modal-ftr">
              <button
                className="puws-btn puws-btn-ghost"
                onClick={() => setShowDiscardPicker(false)}
              >
                <i className="fa-solid fa-xmark" aria-hidden="true"></i> Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ DISCARD PRODUCT MODAL ══
          Same fields/endpoint as InventoryProductList.js's Discard modal
          (PUT /products/:id/discard) -- the backend/feature is unchanged,
          this is only the missing UI for this page. */}
      {discardTarget && (
        <div
          className="puws-overlay"
          onClick={(e) => e.target === e.currentTarget && setDiscardTarget(null)}
        >
          <div className="puws-modal">
            <div className="puws-modal-hdr">
              <h2 className="puws-modal-title">Discard Product</h2>

              <button
                className="puws-modal-close"
                onClick={() => setDiscardTarget(null)}
                aria-label="Close"
                title="Close"
              >
                <i className="fa-solid fa-xmark" aria-hidden="true"></i>
              </button>
            </div>

            <div className="puws-modal-body">
              <div className="puws-unit-info">
                <div>
                  <strong>{discardTarget.name}</strong>
                  {discardTarget.sku ? ` / ${discardTarget.sku}` : ""}
                </div>
                <div className="puws-sku-note">
                  Serial No.: {discardTarget.serial_no || "Not Available"}
                </div>
                <div className="puws-sku-note" data-testid="puws-discard-lifecycle">
                  Purchase Date: {discardTarget.purchase_date ? fmtDateOnly(discardTarget.purchase_date) : "—"}
                  {" • "}Asset Life: {discardTarget.useful_life_label || "—"}
                  {" • "}End Life Date:{" "}
                  {discardTarget.expected_end_date ? fmtDateOnly(discardTarget.expected_end_date) : "—"}
                  {discardTarget.expected_life_override_end_date ? " (revised by Life Review)" : ""}
                </div>
                <div className="puws-sku-note">
                  Life Status: <LifecycleBadge lifecycle={discardTarget.lifecycle} />
                  {discardTarget.remaining_life_label ? ` ${discardTarget.remaining_life_label}` : ""}
                </div>
              </div>

              <div className="puws-form-grid compact">
                <div>
                  <label className="puws-label">
                    Discard Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="puws-input"
                    max={todayInputDate()}
                    value={discardForm.discard_date}
                    onChange={(e) => setDiscardForm((prev) => ({ ...prev, discard_date: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="puws-label">
                    Reason <span className="text-danger">*</span>
                  </label>
                  <select
                    className="puws-form-select"
                    value={discardForm.reason}
                    onChange={(e) => setDiscardForm((prev) => ({ ...prev, reason: e.target.value }))}
                  >
                    <option value="">Select Reason</option>
                    {DISCARD_REASONS.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="puws-label" htmlFor="puws-disposal-method">
                    Disposal Method <span className="text-danger">*</span>
                  </label>
                  <select
                    id="puws-disposal-method"
                    className="puws-form-select"
                    value={discardForm.disposal_method}
                    onChange={(e) => setDiscardForm((prev) => ({ ...prev, disposal_method: e.target.value }))}
                  >
                    <option value="">Select Method</option>
                    {DISPOSAL_METHODS.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="puws-label" htmlFor="puws-auction-date">
                    Auction Date{discardForm.disposal_method === "AUCTION" ? <span className="text-danger"> *</span> : ""}
                  </label>
                  <input
                    id="puws-auction-date"
                    type="date"
                    className="puws-input"
                    value={discardForm.auction_date}
                    onChange={(e) => setDiscardForm((prev) => ({ ...prev, auction_date: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="puws-label" htmlFor="puws-disposal-amount">Auction / Sale Amount</label>
                  <input
                    id="puws-disposal-amount"
                    type="number"
                    min="0"
                    step="0.01"
                    className="puws-input"
                    placeholder="0.00"
                    value={discardForm.disposal_amount}
                    onChange={(e) => setDiscardForm((prev) => ({ ...prev, disposal_amount: e.target.value }))}
                  />
                </div>

                <div className="span2">
                  <label className="puws-label">
                    Remarks{discardForm.reason === "OTHER" ? <span className="text-danger"> *</span> : ""}
                  </label>
                  <textarea
                    className="puws-input"
                    rows={3}
                    value={discardForm.remarks}
                    onChange={(e) => setDiscardForm((prev) => ({ ...prev, remarks: e.target.value }))}
                    placeholder={
                      discardForm.reason === "OTHER"
                        ? "Required when Reason is Other"
                        : "Optional"
                    }
                  />
                </div>
              </div>
            </div>

            <div className="puws-modal-ftr">
              <button
                className="puws-btn puws-btn-ghost"
                onClick={() => setDiscardTarget(null)}
                disabled={discarding}
              >
                <i className="fa-solid fa-arrow-left" aria-hidden="true"></i> Cancel
              </button>

              <button
                className="puws-btn puws-btn-primary"
                onClick={submitDiscard}
                disabled={discarding}
              >
                {discarding ? (
                  <>
                    <Spin white /> Discarding…
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-box-archive" aria-hidden="true"></i> Confirm Discard
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ PRODUCT INVOICE MODAL ══ */}
      {showInvoices && invoiceProduct && (
        <div
          className="puws-overlay"
          onClick={(e) => e.target === e.currentTarget && setShowInvoices(false)}
        >
          <div className="puws-modal puws-view-modal">
            <div className="puws-modal-hdr">
              <h2 className="puws-modal-title">
                {invoiceProduct.name || "Product"} — Invoices
              </h2>

              <button
                className="puws-modal-close"
                onClick={() => setShowInvoices(false)}
                aria-label="Close"
                title="Close"
              >
                <i className="fa-solid fa-xmark" aria-hidden="true"></i>
              </button>
            </div>

            <div className="puws-modal-body">
              <div className="puws-unit-info">
                <div>
                  <strong>Product Items:</strong>{" "}
                  {invoiceProduct.items?.length || 1}
                </div>
                <div className="puws-sku-note">
                  Invoice is linked with product id from product_vendor_invoices.
                </div>
              </div>

              {/* POST .../vendor-invoices requires products:create server-side
                  (routes/productVendorInvoiceRoutes.js) -- a view-only user
                  must never see an "Add Invoice" affordance that can only
                  ever fail. Viewing existing invoices above this point is
                  unaffected -- that only needs products:view, already
                  required to open this modal at all. */}
              {canCreate && (
                <div style={{ marginBottom: 12 }}>
                  <button
                    className="puws-btn puws-btn-primary"
                    onClick={() => setShowInvoiceCreate((v) => !v)}
                  >
                    <i
                      className={`fa-solid ${showInvoiceCreate ? "fa-minus" : "fa-plus"}`}
                      aria-hidden="true"
                    ></i>{" "}
                    {showInvoiceCreate ? "Hide Invoice Form" : "Add Invoice"}
                  </button>
                </div>
              )}

              {canCreate && showInvoiceCreate && (
                <div style={{ marginBottom: 16 }}>
                  {renderInvoiceFields(invoiceForm, setInvoiceField)}

                  <div className="puws-inline-form-actions">
                    <button
                      className="puws-btn puws-btn-ghost"
                      onClick={() => {
                        setInvoiceForm({ ...BLANK });
                        setShowInvoiceCreate(false);
                      }}
                      disabled={savingInvoice}
                    >
                      <i className="fa-solid fa-xmark" aria-hidden="true"></i> Cancel
                    </button>

                    <button
                      className="puws-btn puws-btn-primary"
                      onClick={handleInvoiceCreate}
                      disabled={savingInvoice}
                    >
                      {savingInvoice ? (
                        <>
                          <Spin white /> Uploading…
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-floppy-disk" aria-hidden="true"></i> Save Invoice
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {loadingInvoices ? (
                <div className="puws-loader" style={{ minHeight: 140 }}>
                  <Spin lg />
                  <p>Loading invoices…</p>
                </div>
              ) : invoiceRows.length === 0 ? (
                <div className="puws-empty" style={{ padding: 20 }}>
                  <div className="puws-empty-icon"><i className="fa-solid fa-file-invoice" aria-hidden="true"></i></div>
                  <h5>No invoice found</h5>
                  <p>Add invoice details for this product.</p>
                </div>
              ) : (
                <div className="puws-invoice-preview-layout">
                  <div className="puws-invoice-list-panel">
                    {invoiceRows.map((inv) => (
                      <button
                        key={`${inv.source_product_id}-${inv.id}`}
                        className={`puws-invoice-row-card ${
                          previewInvoice?.id === inv.id &&
                          previewInvoice?.source_product_id === inv.source_product_id
                            ? "active"
                            : ""
                        }`}
                        onClick={() => setPreviewInvoice(inv)}
                      >
                        <div className="puws-invoice-row-top">
                          <strong>{inv.invoice_number || "Invoice"}</strong>
                          <span>{money(inv.amount)}</span>
                        </div>
                        <small>{inv.vendor_company_name || "No vendor"}</small>
                        <small>{fmtDateOnly(inv.purchase_date)}</small>
                      </button>
                    ))}
                  </div>

                  <div className="puws-invoice-preview-panel">
                    {previewInvoice ? (
                      <>
                        <div className="puws-invoice-meta-grid">
                          <div>
                            <span>Invoice No.</span>
                            <strong>{previewInvoice.invoice_number || "—"}</strong>
                          </div>
                          <div>
                            <span>Vendor</span>
                            <strong>{previewInvoice.vendor_company_name || "—"}</strong>
                          </div>
                          <div>
                            <span>Vendor Email</span>
                            <strong>{previewInvoice.vendor_email || "—"}</strong>
                          </div>
                          <div>
                            <span>Vendor Phone</span>
                            <strong>{previewInvoice.vendor_phone || "—"}</strong>
                          </div>
                          <div>
                            <span>Purchase Company</span>
                            <strong>
                              {previewInvoice.purchase_company_name || "—"}
                              {previewInvoice.purchase_company_id
                                ? ` (#${previewInvoice.purchase_company_id})`
                                : ""}
                            </strong>
                          </div>
                          
                          <div>
                            <span>Amount</span>
                            <strong>{money(previewInvoice.amount)}</strong>
                          </div>
                        </div>

                        {previewInvoice.invoice_file ? (
                          <>
                            {previewInvoiceLoading ? (
                              <div className="puws-file-preview-empty">
                                <div><i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i></div>
                                <strong>Loading preview...</strong>
                              </div>
                            ) : previewInvoiceError ? (
                              <div className="puws-file-preview-empty">
                                <div><i className="fa-solid fa-triangle-exclamation" aria-hidden="true"></i></div>
                                <strong>Unable to load preview</strong>
                                <p>{previewInvoiceError}</p>
                              </div>
                            ) : isPdfFile(previewInvoice.invoice_file) && previewInvoiceObjectUrl ? (
                              <iframe
                                title="Invoice Preview"
                                src={previewInvoiceObjectUrl}
                                className="puws-invoice-frame"
                              />
                            ) : isImageFile(previewInvoice.invoice_file) && previewInvoiceObjectUrl ? (
                              <img
                                src={previewInvoiceObjectUrl}
                                alt="Invoice Preview"
                                className="puws-invoice-img"
                              />
                            ) : (
                              <div className="puws-file-preview-empty">
                                <div><i className="fa-solid fa-file" aria-hidden="true"></i></div>
                                <strong>{getFileName(previewInvoice.invoice_file)}</strong>
                                <p>Preview is not available for this file type.</p>
                              </div>
                            )}

                            <button
                              type="button"
                              onClick={handleDownloadInvoice}
                              className="puws-btn puws-btn-primary puws-open-file-btn"
                              disabled={previewInvoiceLoading}
                            >
                              <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i>{" "}
                              Open Invoice File
                            </button>
                          </>
                        ) : (
                          <div className="puws-file-preview-empty">
                            <div><i className="fa-solid fa-file-invoice" aria-hidden="true"></i></div>
                            <strong>No invoice file uploaded</strong>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="puws-file-preview-empty">
                        <div><i className="fa-solid fa-file-invoice" aria-hidden="true"></i></div>
                        <strong>Select invoice to preview</strong>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="puws-modal-ftr">
              <button
                className="puws-btn puws-btn-ghost"
                onClick={() => setShowInvoices(false)}
              >
                <i className="fa-solid fa-xmark" aria-hidden="true"></i> Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ──────────────────────────────── */}
      {toast.show && (
        <div className={`puws-toast ${toast.variant}`}>
          <i
            className={`fa-solid ${
              toast.variant === "success" ? "fa-circle-check" : "fa-circle-xmark"
            }`}
            aria-hidden="true"
          ></i>{" "}
          {toast.msg}
        </div>
      )}
    </div>
  );
}